import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import { Pencil, UserPlus, Trash2, Search, CalendarPlus, UserCheck, UserX, KeyRound, MessageCircle, Clock, Download, Upload, FileSpreadsheet } from 'lucide-react';
import { useAuth } from '../context/useAuth';
import { useI18n } from '../context/useI18n';
import { emitToast } from '../context/toast';
import InviteCodeCell from './admin/InviteCodeCell';
import TrainerBrandingModal from './admin/TrainerBrandingModal';
import ExtendVigenciaModal from './admin/ExtendVigenciaModal';
import UserActivateModal from './admin/UserActivateModal';
import MarkPendingPaymentModal from './admin/MarkPendingPaymentModal';
import { buildWaMeUrl } from '../utils/whatsappSupport';
import CreateVirtualGymModal from './admin/CreateVirtualGymModal';
import EditUserModal from './admin/EditUserModal';
import GymMinimalBrandingFields from './admin/GymMinimalBrandingFields';
import PhoneField from './ui/PhoneField';
import { OrgProvisionSuccessModal } from './admin/OrgOperatorPanel';
import { UserAssignmentsPanel } from './AdminCompanies';
import { FitLiveTrainersRoster } from '../fit-live/FitLiveAdminSurfaces';
import { isFitLiveUiEnabled } from '../utils/fitLiveModule';
import {
  filterUsersByScope,
  SCOPE_META,
  SUPER_ADMIN_DOMAIN_TABS,
  getScopeMeta,
  getSuperAdminDomainTabs,
  isD28dDomainUser,
  isFoodOnlyDomainUser,
  isFoodDomainUser,
  isTrainingDomainUser,
  isFoodCommercialPlan,
  foodOnlyModuleAccess,
  wantsD28dProductAccess,
  hasFoodNestLink,
  summarizeFoodRoster,
} from '../utils/adminUserScopes';
import { CANONICAL_ASSIGNABLE_ROLES, filterAssignableRoles } from '../utils/canonicalAssignRoles';
import { isPostpartumMom } from '../utils/postpartumFlag';
import { userRoles, makeHasAnyRole } from './dashboard/roles';
import { canCreateChildSedesUser } from '../utils/gymRoleScope';
import {
  isPlatformSeedGym,
  isPlatformTemplateGym,
  isD28dPlatformGym,
  dedupeGymsById,
  gymSelectOptionLabel,
  gymsForMemberAssignment,
  gymsForD28dAdminAssignment,
} from '../utils/organizationView';
import { filterPurchasableCycles, formatCycleDate, resolveCycleEnd, pickDefaultCycleId } from '../utils/cycleUtils';
import {
  consecutiveCyclePack,
  cycleIdsFromPack,
  planCyclesIncluded,
} from '../utils/planCyclePack';

function adminCycleOptionLabel(c) {
  if (!c) return '';
  const start = formatCycleDate(c.startDate || c.start_date);
  const end = formatCycleDate(c.endDate || c.end_date || resolveCycleEnd(c));
  const range = start && end ? ` (${start} – ${end})` : (start ? ` (${start})` : '');
  return `${c.name || `Ciclo ${c.id}`}${range}`;
}
const EMPTY_MODULE_ACCESS = {
  gym: false,
  d28d: false,
  training: false,
  nutrition: false,
  food_plan: false,
  live_classes: false,
};

const MODULE_OPTIONS = [
  { key: 'gym', label: 'Gimnasio (producto)' },
  { key: 'd28d', label: 'Programas D28D' },
  { key: 'training', label: 'Entrenadores' },
  { key: 'food_plan', label: 'Plan de alimentación' },
  { key: 'live_classes', label: 'Clases en vivo' },
];

/** Roles asignables desde UI cuando el actor es coach (sin permisos globales). */
const COACH_ASSIGNABLE_ROLES = [
  { value: 'usuario_final', label: 'Usuario Final' },
  { value: 'nutricionista', label: 'Nutricionista' },
];

/** Roles que admin_d28d puede asignar en usuarios del dominio D28D. */
const D28D_ADMIN_ASSIGNABLE_ROLES = [
  { value: 'usuario_final', label: 'Usuario final' },
  { value: 'usuario_final_sede', label: 'Usuario final sede' },
  { value: 'entrenador_d28d', label: 'Entrenador host D28D' },
  { value: 'entrenador_gym', label: 'Entrenador host gym' },
  { value: 'admin_gym_virtual', label: 'Admin gym virtual' },
  { value: 'admin_sede', label: 'Admin sede' },
];

const ALL_ASSIGNABLE_ROLES = CANONICAL_ASSIGNABLE_ROLES;

const GYM_FINAL_USER_MODULE_ACCESS = {
  gym: true,
  d28d: true,
  live_classes: true,
  training: false,
  nutrition: false,
  food_plan: false,
};

/** Módulos al crear cliente desde panel coach (sin FOOD; se compra en Mi cuenta). */
const COACH_CREATE_MODULE_ACCESS = {
  gym: false,
  d28d: false,
  training: true,
  nutrition: false,
  food_plan: false,
  live_classes: false,
};

const emptyForm = () => ({
  nombre: '',
  email: '',
  password: '',
  telefono: '',
  roles: ['usuario_final'],
  gym_id: '',
  trainer_id: '',
  planId: '',
  cycle_id: '',
  cycle_ids: [],
  module_access: { ...EMPTY_MODULE_ACCESS },
  copy_gallery_from_nico: true,
  copy_routines_from_nico: true,
  gymCreateKind: 'usuario_final',
  sede_ciudad: '',
  sede_admin_email: '',
  sede_admin_password: '',
  sede_brand_name: '',
  sede_logo_url: '',
  sede_primary_color: '#2563eb',
  sede_secondary_color: '#10b981',
  sede_support_whatsapp: '',
  sede_welcome_message: '',
  sede_calendar_program_ids: [],
  sede_welcome_videos_by_program: {},
  sede_member_fee_cop: '',
  sede_plan_id: '',
  sede_skip_member_payment: true,
  sede_allow_food_addon: true,
  d28d_program: '',
  d28d_addon_plan: '',
  editKind: 'user',
  sede_gym_id: '',
  sede_nombre: '',
  sede_contact_email: '',
  sede_invite_code: '',
  extend_vigencia_days: '',
  fecha_inicio: '',
  fecha_vencimiento: '',
  tiene_restricciones: false,
  restricciones_detalles: '',
  soy_mama_posparto: false,
});

const formatRoleLabel = (rol) => {
  if (!rol) return '-';
  return String(rol).replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

function userGymId(user) {
  const raw = user?.gym_id ?? user?.gymId;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function rolesOfUser(user) {
  return user.roles?.length ? user.roles : [user.rol].filter(Boolean);
}

function isPlatformStaffUser(user) {
  return rolesOfUser(user).some((r) => ['admin_d28d', 'entrenador_d28d', 'super_admin'].includes(r));
}

/** Usuario final consumidor D28D (plataforma o sede). */
function isD28dConsumerFinalUser(user, { platformOnly = false } = {}) {
  const roles = rolesOfUser(user);
  if (platformOnly) return roles.includes('usuario_final');
  return roles.includes('usuario_final') || roles.includes('usuario_final_sede');
}

function userGymDisplayName(user, gymById, t) {
  const gid = userGymId(user);
  if (gid && gymById[gid]?.nombre) {
    const gym = gymById[gid];
    const meta = gymKindMeta(gym, t);
    return `${gymDisplayLabel(gym)} (${meta.label})`;
  }
  const roles = user.roles?.length ? user.roles : [user.rol].filter(Boolean);
  if (roles.includes('admin_d28d') || roles.includes('entrenador_d28d')) {
    return t('users.platform_d28d', 'Plataforma D28D');
  }
  if (roles.some((r) => ['admin_marca', 'admin_gimnasio', 'admin_gym', 'admin_sede'].includes(r))) {
    return t('users.gym_unassigned', 'Sin sede asignada');
  }
  if (roles.includes('super_admin')) {
    return t('users.platform_global', 'Plataforma global');
  }
  return '—';
}

/** Training (independiente de D28D): nombre del coach asignado. */
function userTrainerDisplayName(user, trainerById, t) {
  const tid = user?.trainer_id ?? user?.trainerId;
  if (tid == null || tid === '') return t('users.coach_unassigned', 'Sin coach');
  const trainer = trainerById[Number(tid)];
  if (trainer?.nombre) return trainer.nombre;
  return t('users.coach_id', 'Coach #{id}').replace('{id}', String(tid));
}

function userTrainerInviteCode(user, trainerById) {
  const tid = user?.trainer_id ?? user?.trainerId;
  if (tid == null || tid === '') return null;
  return trainerById[Number(tid)]?.invite_code || null;
}

/** Gym ancla D28D marca blanca (operativo en listados). */
function isD28dAnchorGym(gym) {
  return isD28dPlatformGym(gym);
}

function gymKindMeta(gym, t) {
  if (!gym) return { label: '—', badgeClass: 'badge-slate', rowClass: '' };
  if (isD28dPlatformGym(gym)) {
    return {
      label: t('gyms.kind_d28d', 'D28D'),
      badgeClass: 'badge-purple',
      rowClass: '',
    };
  }
  if (isPlatformTemplateGym(gym)) {
    return {
      label: t('gyms.kind_seed_internal', 'D28D (interno)'),
      badgeClass: 'badge-purple',
      rowClass: 'opacity-60',
    };
  }
  if (gym.parent_gym_id != null && gym.parent_gym_id !== '') {
    return {
      label: t('gyms.kind_sede', 'Sede física'),
      badgeClass: 'badge-teal',
      rowClass: 'bg-teal-50/40',
    };
  }
  return {
    label: t('gyms.kind_virtual', 'Gym virtual'),
    badgeClass: 'badge-blue',
    rowClass: 'bg-sky-50/30',
  };
}

function gymDisplayLabel(gym) {
  if (!gym) return '—';
  return gym.brand_name || gym.nombre || `Gym #${gym.id}`;
}

const SUBSCRIPTION_ESTADO_BADGE = {
  activo: 'badge-green',
  pendiente_sede: 'badge-warning',
  pendiente_pago_online: 'badge-warning',
  pendiente_habilitacion: 'badge-warning',
  pendiente_activacion: 'badge-warning',
  pendiente: 'badge-warning',
  vencido: 'badge-danger',
  cancelado: 'badge-danger',
  rechazado: 'badge-danger',
};

function isConsumerUserRow(user) {
  const roles = user.roles?.length ? user.roles : [user.rol].filter(Boolean);
  return roles.includes('usuario_final') || roles.includes('usuario_final_sede');
}

function consumerNeedsActivation(user) {
  if (!isConsumerUserRow(user)) return false;
  const sub = user.subscription || {};
  const estado = String(sub.estado || '').toLowerCase();
  if (sub.pending || sub.pending_registration) return true;
  if (estado === 'pendiente_habilitacion') return true;
  if (sub.has_account && (sub.pending || !sub.active)) return true;
  return false;
}

function operatorNeedsActivation(user, gymById, gyms = []) {
  if (!isSedeOperatorUser(user, gymById, gyms)) return false;
  if (hasActiveOperatorLicenses(user?.licenses)) return false;
  const sub = user.subscription || {};
  if (sub.operator_license && sub.active) return false;
  return true;
}

function userNeedsActivation(user, gymById, gyms = []) {
  return consumerNeedsActivation(user) || operatorNeedsActivation(user, gymById, gyms);
}

/** Días restantes de vigencia (para prellenar Activar tras desactivar). */
function remainingVigenciaDays(user) {
  const sub = user?.subscription || {};
  const fromSub = Number(sub.days_left);
  if (Number.isFinite(fromSub) && fromSub >= 1 && fromSub <= 365) return Math.ceil(fromSub);
  const venc = sub.fecha_vencimiento || sub.payment_due_date;
  if (venc) {
    const left = Math.ceil((new Date(venc).getTime() - Date.now()) / 86400000);
    if (Number.isFinite(left) && left >= 1 && left <= 365) return left;
  }
  // Fallback: licencia core con valid_until (si la cuenta no vino enriquecida).
  const licUntil = (user?.licenses || [])
    .filter((l) => ['d28d', 'gym', 'live_classes'].includes(String(l.module_code || '')))
    .map((l) => l.valid_until)
    .filter(Boolean)
    .map((d) => new Date(d).getTime())
    .filter((n) => Number.isFinite(n));
  if (licUntil.length) {
    const left = Math.ceil((Math.max(...licUntil) - Date.now()) / 86400000);
    if (Number.isFinite(left) && left >= 1 && left <= 365) return left;
  }
  return 30;
}

function formatSubscriptionEstado(sub, t) {
  if (sub?.operator_license && sub?.active && !sub?.has_account) {
    return t('users.sub_active', 'Activo');
  }
  if (!sub?.has_account && !sub?.operator_license) {
    if (sub?.pending || sub?.pending_registration) {
      return t('users.sub_pending', 'Pendiente de activación');
    }
    return t('users.sub_operator_pending', 'Pendiente vigencia operador');
  }
  const estado = String(sub?.estado || '').toLowerCase();
  if (sub?.pending || [
    'pendiente_activacion', 'pendiente_habilitacion', 'pendiente_sede',
    'pendiente_pago_online', 'pendiente',
  ].includes(estado)) {
    return t('users.sub_pending', 'Pendiente de activación');
  }
  if (estado === 'activo') return t('users.sub_active', 'Activo');
  if (estado === 'vencido') return t('users.sub_expired', 'Vencido');
  if (estado === 'rechazado') return t('users.sub_rejected', 'Rechazado');
  return sub?.estado || '—';
}

function formatDaysLeft(sub, t) {
  if (sub?.operator_license && sub?.active && sub.days_left != null) {
    if (sub.days_left < 0) {
      return t('users.days_overdue', 'Vencido ({n}d)').replace('{n}', String(Math.abs(sub.days_left)));
    }
    if (sub.days_left === 0) return t('users.days_today', 'Vence hoy');
    return `${sub.days_left}d`;
  }
  if (!sub?.has_account && !sub?.operator_license) return '—';
  if (!sub?.has_account) return '—';
  if (sub.pending) return '—';
  if (sub.days_left == null) return '—';
  if (sub.days_left < 0) {
    return t('users.days_overdue', 'Vencido ({n}d)').replace('{n}', String(Math.abs(sub.days_left)));
  }
  if (sub.days_left === 0) return t('users.days_today', 'Vence hoy');
  return `${sub.days_left}d`;
}

function gymScopedRoleLabel(user, gymById = {}) {
  const roles = userRolesList(user);
  const gid = userGymId(user);
  const gym = gid ? gymById[gid] : null;
  if (roles.includes('admin_sede')) return 'Admin sede';
  if (gym?.parent_gym_id != null && roles.includes('admin_gym_virtual')) {
    return 'Gym virtual (sede — corregir)';
  }
  if (gym?.parent_gym_id != null && roles.some((r) => ['admin_gimnasio', 'admin_marca', 'admin_gym'].includes(r))) {
    return 'Admin sede';
  }
  if (roles.some((r) => ['admin_gimnasio', 'admin_marca', 'admin_gym', 'admin_gym_virtual'].includes(r))) {
    return 'Admin gym virtual';
  }
  if (gym?.parent_gym_id != null) {
    const gymEmail = String(gym.email || '').trim().toLowerCase();
    const userEmail = String(user.email || '').trim().toLowerCase();
    if (gymEmail && userEmail === gymEmail) return 'Admin sede (pendiente)';
    if (userEmail === `admin.gym${gid}@foodplan.local`.toLowerCase()) return 'Admin sede (pendiente)';
  }
  return 'Usuario final';
}

function userRolesList(user) {
  return user.roles?.length ? user.roles : [user.rol].filter(Boolean);
}

function formGymId(formData = {}) {
  const raw = formData?.gym_id ?? formData?.gymId;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function activationFormContext(user, formData = {}, editingUser = null) {
  if (editingUser?.id === user?.id) return formData;
  return {
    ...formData,
    trainer_id: user?.trainer_id || '',
    gym_id: user?.gym_id ?? user?.gymId ?? '',
    module_access: user?.module_access || formData?.module_access,
  };
}

/** Cliente Training = coach asignado. Independiente de D28D. */
function isTrainingCoachClient(user, formData = {}) {
  const roles = Array.isArray(formData.roles) && formData.roles.length
    ? formData.roles
    : (user?.roles?.length ? user.roles : [user?.rol].filter(Boolean));
  if (!roles.includes('usuario_final')) return false;
  const trainerId = formData.trainer_id || user?.trainer_id;
  return Boolean(trainerId);
}

function isSedeConsumerUserRow(user, gymById, gyms = []) {
  if (!isConsumerUserRow(user)) return false;
  const gid = userGymId(user);
  if (!gid) return false;
  const gym = gymById[gid] || gyms.find((g) => Number(g.id) === Number(gid));
  return Boolean(gym?.parent_gym_id);
}

/** Días de vigencia al guardar/activar: usa el campo o 30 por defecto si el usuario está pendiente. */
function resolveActivationDays(formData, user) {
  const n = Number(formData?.extend_vigencia_days);
  if (Number.isFinite(n) && n >= 1 && n <= 365) return n;
  const sub = user?.subscription || {};
  const needsActivation = sub.pending || sub.pending_registration || !sub.active || !sub.has_account;
  return needsActivation ? 30 : null;
}

/** YYYY-MM-DD civil (Bogotá) para payload de vigencia. */
function dateInputValue(value) {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Bogota',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

function todayLocalISO() {
  return dateInputValue(new Date());
}

function addDaysLocalISO(iso, days) {
  const base = iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? iso.slice(0, 10) : todayLocalISO();
  const d = new Date(`${base}T12:00:00`);
  d.setDate(d.getDate() + (Number(days) || 0));
  return dateInputValue(d);
}

function resolveVigenciaFormDefaults(user, daysHint = 30) {
  const sub = user?.subscription || {};
  const days = Number(daysHint) || 30;
  const inicio = dateInputValue(sub.fecha_inicio || sub.cycle_enable_date || sub.cycle_start_date)
    || todayLocalISO();
  const fin = dateInputValue(sub.fecha_vencimiento) || addDaysLocalISO(inicio, days);
  return { fecha_inicio: inicio, fecha_vencimiento: fin };
}

function vigenciaPayloadFromForm(formData, days) {
  const start = dateInputValue(formData?.fecha_inicio) || todayLocalISO();
  const end = dateInputValue(formData?.fecha_vencimiento) || addDaysLocalISO(start, days || 30);
  return {
    fecha_inicio: `${start}T12:00:00.000-05:00`,
    fecha_vencimiento: `${end}T23:59:59.000-05:00`,
  };
}

async function activateUserSubscription(user, { days, plan, cycle_id, formData, editingUser, gymById = {}, gyms = [], finance_zero = false }) {
  const vigenciaDays = Number(days) || 30;
  const isTraining = isTrainingCoachClient(user, formData || editingUser);
  if (isTraining) {
    return api.post(`/training/admin/clients/${user.id}/activate`, {
      days: vigenciaDays,
      activate_food: false,
      ...(finance_zero ? { finance_zero: true } : {}),
    });
  }
  const isSedeOp = isSedeOperatorUser(user, gymById, gyms)
    || formData?.editKind === 'sede'
    || editingUser?.id === user?.id && formData?.sede_gym_id;
  const isSedeConsumer = isSedeConsumerUserRow(user, gymById, gyms);
  const dates = vigenciaPayloadFromForm(formData || {}, vigenciaDays);
  const planName = String(plan || user?.subscription?.plan || user?.planId || '').trim();
  const planKind = String(
    user?.subscription?.kind
    || user?.subscription?.plan_kind
    || user?.plan_kind
    || '',
  ).toLowerCase();
  const foodOnly = isFoodOnlyDomainUser(user)
    || planKind === 'food'
    || /^FOOD/i.test(planName);
  const moduleCode = foodOnly ? 'food' : 'd28d';
  const payload = {
    days: vigenciaDays,
    module_code: moduleCode,
    ...dates,
    ...(plan ? { plan } : {}),
    ...(cycle_id && !foodOnly ? { cycle_id } : {}),
    ...(finance_zero ? { finance_zero: true } : {}),
  };
  if (isSedeOp && !user.subscription?.has_account && !plan) {
    return api.post(`/payment-admin/users/${user.id}/activate`, payload);
  }
  if (isSedeConsumer && !plan) {
    return api.post(`/payment-admin/users/${user.id}/activate`, payload);
  }
  return api.post(`/payment-admin/users/${user.id}/activate`, {
    ...payload,
    plan: plan || undefined,
  });
}

function hasActiveOperatorLicenses(licenses = []) {
  const core = new Set(['d28d', 'gym', 'live_classes']);
  const now = Date.now();
  return licenses.some((lic) => {
    if (!lic?.active || !core.has(lic.module_code)) return false;
    if (lic.valid_from) {
      const from = new Date(lic.valid_from).getTime();
      if (Number.isFinite(from) && from > now) return false;
    }
    if (!lic.valid_until) return true;
    return new Date(lic.valid_until).getTime() >= now;
  });
}

function isSedeOperatorUser(user, gymById, gyms = []) {
  const roles = userRolesList(user);
  const gid = userGymId(user);
  if (!gid) return false;
  const gym = gymById[gid] || gyms.find((g) => Number(g.id) === Number(gid));
  if (!gym?.parent_gym_id) return false;
  const operatorRoles = [
    'admin_sede', 'admin_gimnasio', 'admin_gym', 'admin_marca', 'admin_gym_virtual',
  ];
  if (roles.some((r) => operatorRoles.includes(r))) return true;
  const gymEmail = String(gym.email || '').trim().toLowerCase();
  const userEmail = String(user.email || '').trim().toLowerCase();
  if (gymEmail && userEmail === gymEmail) return true;
  return userEmail === `admin.gym${gid}@foodplan.local`.toLowerCase();
}

function paymentReminderWaUrl(user, subscription, t) {
  const phone = user?.telefono;
  if (!phone) return null;
  const due = subscription?.payment_due_date || subscription?.fecha_vencimiento;
  const dueLabel = due ? new Date(due).toLocaleDateString('es-CO') : t('users.pending_soon', 'pronto');
  const msg = `Hola ${user.nombre || ''}, te recordamos completar tu pago pendiente${subscription?.plan ? ` del plan ${subscription.plan}` : ''}. Fecha límite: ${dueLabel}.`;
  return buildWaMeUrl(phone, msg);
}

/** WhatsApp al teléfono del consumidor (usuario_final / usuario_final_sede). */
function consumerContactWaUrl(user, subscription, t, { needsActivation = false } = {}) {
  if (!isConsumerUserRow(user)) return null;
  if (needsActivation) return paymentReminderWaUrl(user, subscription, t);
  const phone = user?.telefono;
  if (!phone) return null;
  const plan = subscription?.plan || user?.planId;
  const msg = plan
    ? `Hola ${user.nombre || ''}, te escribimos de ${plan}.`
    : `Hola ${user.nombre || ''}, te escribimos de soporte.`;
  return buildWaMeUrl(phone, msg);
}

function userPlanRecord(user, plans) {
  const planName = user.subscription?.plan || user.planId;
  if (!planName) return null;
  const gymId = user.gym_id ?? user.gymId ?? null;
  const matches = plans.filter((p) => {
    const display = p.display_nombre || p.nombre;
    return display === planName || p.nombre === planName;
  });
  if (!matches.length) return null;
  if (gymId != null) {
    const gymPlan = matches.find((p) => Number(p.gym_id) === Number(gymId));
    if (gymPlan) return gymPlan;
  }
  return matches.find((p) => p.gym_id == null) || matches[0];
}

/** Etiqueta pareja en Usuarios: “Pareja de X” / “Titular · pareja: Y”. */
function coupleLinkLabel(user, allUsers = []) {
  const sub = user?.subscription || {};
  if (!sub.is_couple) return null;
  const findName = (id) => {
    if (id == null) return null;
    const row = (allUsers || []).find((u) => Number(u.id) === Number(id));
    return row?.nombre || null;
  };
  if (sub.couple_role === 'invitado') {
    const primaryName = findName(sub.couple_primary_user_id);
    return primaryName
      ? `Pareja de ${primaryName}`
      : 'Pareja (invitado)';
  }
  if (sub.couple_role === 'titular') {
    const partnerName = findName(sub.couple_partner_user_id);
    if (partnerName) return `Titular · pareja: ${partnerName}`;
    return sub.couple_invite_used ? 'Titular (pareja)' : 'Titular (sin pareja aún)';
  }
  return 'Plan pareja';
}

function isD28dConsumerUser(user, plans) {
  if (!isConsumerUserRow(user)) return false;
  const plan = userPlanRecord(user, plans);
  if (plan) return String(plan.kind || 'd28d') === 'd28d';
  return Boolean(user.module_access?.d28d || user.module_access?.live_classes);
}

function subscriptionSortRank(user, gymById, gyms) {
  const pending = userNeedsActivation(user, gymById, gyms) ? 0 : 1;
  const sub = user.subscription || {};
  const days = Number.isFinite(sub.days_left) ? Number(sub.days_left) : 99999;
  return { pending, days };
}

function showUserSubscriptionCols(user, gymById, gyms = []) {
  return isConsumerUserRow(user) || isSedeOperatorUser(user, gymById, gyms);
}

function isSedeAdminUserRow(user, gymById) {
  return isSedeOperatorUser(user, gymById, []);
}

const SEDE_OPERATOR_ROLES = [
  'admin_sede', 'admin_gimnasio', 'admin_gym', 'admin_marca', 'admin_gym_virtual',
];

function resolveSedeAdminUser(sedeGym, users = []) {
  if (!sedeGym?.id) return null;
  const gid = Number(sedeGym.id);
  const gymUsers = users.filter((u) => userGymId(u) === gid);
  const operator = gymUsers.find((u) => userRolesList(u).some((r) => SEDE_OPERATOR_ROLES.includes(r)));
  if (operator) return operator;
  const gymEmail = String(sedeGym.email || '').trim().toLowerCase();
  if (gymEmail) {
    const byEmail = gymUsers.find((u) => String(u.email || '').trim().toLowerCase() === gymEmail);
    if (byEmail) return byEmail;
  }
  const systemEmail = `admin.gym${gid}@foodplan.local`.toLowerCase();
  const systemAdmin = gymUsers.find((u) => String(u.email || '').trim().toLowerCase() === systemEmail);
  if (systemAdmin) return systemAdmin;
  return null;
}

function isSedeAdminCandidateUser(user, gymById, gyms = []) {
  const gid = userGymId(user);
  if (!gid) return false;
  const gym = gymById[gid] || gyms.find((g) => Number(g.id) === Number(gid));
  if (!gym?.parent_gym_id) return false;
  if (isSedeOperatorUser(user, gymById, gyms)) return true;
  const gymEmail = String(gym.email || '').trim().toLowerCase();
  const userEmail = String(user.email || '').trim().toLowerCase();
  if (gymEmail && userEmail === gymEmail) return true;
  return userEmail === `admin.gym${gid}@foodplan.local`.toLowerCase();
}

function isSedeManagedUserRow(user, gymById, gyms = [], scopeGymId = null) {
  return isSedeAdminCandidateUser(user, gymById, gyms);
}

function buildSedeFormFromGym(baseForm, sedeGym, gid, parentProgramsList = []) {
  const calendarIds = Array.isArray(sedeGym?.calendar_program_ids) && sedeGym.calendar_program_ids.length
    ? sedeGym.calendar_program_ids
    : parentProgramsList.map((p) => p.id);
  return {
    ...baseForm,
    editKind: 'sede',
    sede_gym_id: String(gid),
    sede_nombre: sedeGym?.nombre || '',
    sede_contact_email: sedeGym?.email || '',
    sede_ciudad: sedeGym?.ciudad || '',
    sede_invite_code: sedeGym?.invite_code || '',
    sede_brand_name: sedeGym?.brand_name || sedeGym?.nombre || '',
    sede_logo_url: sedeGym?.logo_url || '',
    sede_primary_color: sedeGym?.primary_color || '#2563eb',
    sede_secondary_color: sedeGym?.secondary_color || '#10b981',
    sede_support_whatsapp: sedeGym?.support_whatsapp || '',
    sede_welcome_message: sedeGym?.welcome_message || '',
    sede_calendar_program_ids: calendarIds,
    sede_welcome_videos_by_program: (sedeGym?.welcome_videos_by_program
      && typeof sedeGym.welcome_videos_by_program === 'object'
      && !Array.isArray(sedeGym.welcome_videos_by_program))
      ? { ...sedeGym.welcome_videos_by_program }
      : {},
    sede_member_fee_cop: sedeGym?.member_fee_cop != null ? String(sedeGym.member_fee_cop) : '',
    sede_skip_member_payment: sedeGym?.skip_member_payment === true,
    sede_allow_food_addon: sedeGym?.allow_food_addon !== false,
    sede_plan_id: Array.isArray(sedeGym?.available_plan_ids) && sedeGym.available_plan_ids.length
      ? String(sedeGym.available_plan_ids[0])
      : (sedeGym?.plan_id ? String(sedeGym.plan_id) : ''),
  };
}

export default function AdminUsers({
  scope = 'default',
  scopeGymId = null,
  scopeGymName = '',
  initialViewTab = null,
  pendingActivateUserId = null,
  onPendingActivateConsumed = null,
}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const { user: currentUser } = useAuth();
  const { t } = useI18n();
  const [gyms, setGyms] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [viewTab, setViewTab] = useState(() => {
    if (initialViewTab === 'gyms' || initialViewTab === 'users') return initialViewTab;
    return 'users';
  }); // 'users' | 'gyms' | 'trainers'
  const [showAssignedModal, setShowAssignedModal] = useState(false);
  const [assignedTitle, setAssignedTitle] = useState('');
  const [assignedList, setAssignedList] = useState([]);
  const [brandingTrainer, setBrandingTrainer] = useState(null);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [trainerEditForm, setTrainerEditForm] = useState({
    nombre: '',
    email: '',
    especialidad: '',
    capacidad_usuarios: 50,
  });
  const [trainerEditSaving, setTrainerEditSaving] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [promoFilter, setPromoFilter] = useState('all');
  const [exportStatusPrompt, setExportStatusPrompt] = useState(false);
  const [excelBusy, setExcelBusy] = useState(false);
  const usersImportInputRef = useRef(null);
  const [searchGym, setSearchGym] = useState('');
  const [searchTrainer, setSearchTrainer] = useState('');
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendSaving, setExtendSaving] = useState(false);
  const [activateTarget, setActivateTarget] = useState(null);
  const [activateSaving, setActivateSaving] = useState(false);
  const [pendingTarget, setPendingTarget] = useState(null);
  const [pendingSaving, setPendingSaving] = useState(false);
  const [usersWorkspace, setUsersWorkspace] = useState('directory');
  const [showCreateSedeModal, setShowCreateSedeModal] = useState(false);
  const [sedeProvisionResult, setSedeProvisionResult] = useState(null);
  const [parentSedePrograms, setParentSedePrograms] = useState([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [subscriptionActivating, setSubscriptionActivating] = useState(false);
  const [catalogRoles, setCatalogRoles] = useState([]);

  // Form state
  const [formData, setFormData] = useState(emptyForm);

  const actorRoles = useMemo(
    () => (Array.isArray(currentUser?.roles) && currentUser.roles.length
      ? currentUser.roles
      : [currentUser?.rol].filter(Boolean)),
    [currentUser],
  );
  const hasAnyRole = useMemo(() => makeHasAnyRole(actorRoles), [actorRoles]);
  /** Alineado con backend isCoachUser: sin exigir gimnasio; alta ligada al trainer_id. */
  const isCoachActor = useMemo(
    () => actorRoles.some((r) => ['entrenador', 'nutricionista', 'admin_training', 'admin_entrenador'].includes(r))
      && !actorRoles.some((r) => [
        'super_admin', 'admin_d28d', 'admin_gimnasio', 'admin_marca', 'admin_gym', 'admin_sede',
      ].includes(r)),
    [actorRoles],
  );
  const coachTrainerId = useMemo(() => {
    const tid = Number(currentUser?.trainer_id ?? currentUser?.trainerId);
    return Number.isFinite(tid) && tid > 0 ? tid : null;
  }, [currentUser?.trainer_id, currentUser?.trainerId]);
  const coachModuleOptions = useMemo(
    () => MODULE_OPTIONS.filter((m) => !['gym', 'd28d', 'live_classes'].includes(m.key)),
    [],
  );
  const isSuperAdmin = hasAnyRole(['super_admin']);
  const isPlatformD28dActor = hasAnyRole(['admin_d28d']) && !isSuperAdmin;

  useEffect(() => {
    if (!isSuperAdmin) {
      setCatalogRoles([]);
      return undefined;
    }
    let cancelled = false;
    api.get('/admin/platform-roles')
      .then((res) => {
        if (!cancelled) setCatalogRoles(res.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setCatalogRoles([]);
      });
    return () => { cancelled = true; };
  }, [isSuperAdmin]);

  const roleOptionsForForm = useMemo(() => {
    if (isCoachActor) return COACH_ASSIGNABLE_ROLES;
    if (isPlatformD28dActor && !isSuperAdmin) return D28D_ADMIN_ASSIGNABLE_ROLES;
    const currentCodes = formData.roles?.length
      ? formData.roles
      : (editingUser ? (editingUser.roles || [editingUser.rol]) : []);
    if (isSuperAdmin && catalogRoles.length) {
      return filterAssignableRoles(
        catalogRoles
          .filter((r) => r.active !== false)
          .map((r) => ({ value: r.code, label: r.label || r.name || r.code })),
        currentCodes,
      );
    }
    return filterAssignableRoles(ALL_ASSIGNABLE_ROLES, currentCodes);
  }, [isCoachActor, isSuperAdmin, isPlatformD28dActor, catalogRoles, formData.roles, editingUser]);

  const gymById = useMemo(
    () => Object.fromEntries(gyms.map((g) => [Number(g.id), g])),
    [gyms],
  );
  const trainerById = useMemo(() => {
    const map = Object.fromEntries(trainers.map((t) => [Number(t.id), t]));
    // Coach: asegurar su propia ficha (nombre + invite) aunque el listado venga filtrado/parcial.
    if (isCoachActor) {
      const tid = coachTrainerId ?? Number(currentUser?.trainer_id ?? currentUser?.trainerId);
      if (Number.isFinite(tid) && tid > 0) {
        const prev = map[tid] || {};
        map[tid] = {
          ...prev,
          id: tid,
          nombre: prev.nombre || currentUser?.nombre || currentUser?.brand_name || `Coach #${tid}`,
          invite_code: prev.invite_code
            || currentUser?.trainer_invite_code
            || currentUser?.invite_code
            || null,
        };
      }
    }
    return map;
  }, [trainers, isCoachActor, currentUser, coachTrainerId]);
  const coachTrainerLabel = useMemo(() => {
    if (!isCoachActor) return '';
    const tr = coachTrainerId != null ? trainerById[coachTrainerId] : null;
    return tr?.nombre || tr?.brand_name || currentUser?.nombre
      || (coachTrainerId ? `Coach #${coachTrainerId}` : 'Tu marca de entrenador');
  }, [isCoachActor, coachTrainerId, trainerById, currentUser?.nombre]);

  const canDeleteUsers = useMemo(
    () => hasAnyRole(['super_admin', 'admin_d28d', 'admin_gimnasio', 'admin_marca', 'admin_gym', 'admin_sede']),
    [hasAnyRole],
  );
  const canManageSubscription = useMemo(
    () => !isCoachActor && hasAnyRole(['super_admin', 'admin_d28d', 'admin_gimnasio', 'admin_marca', 'admin_gym', 'admin_sede']),
    [hasAnyRole, isCoachActor],
  );
  const isGymOperator = useMemo(
    () => hasAnyRole(['admin_gym_virtual', 'admin_gimnasio', 'admin_marca', 'admin_gym', 'admin_sede'])
      && !isSuperAdmin
      && !isPlatformD28dActor,
    [hasAnyRole, isSuperAdmin, isPlatformD28dActor],
  );
  const isSedeActor = useMemo(() => hasAnyRole(['admin_sede']), [hasAnyRole]);
  const actorGymId = useMemo(() => {
    const raw = currentUser?.gym_id
      ?? currentUser?.gymId
      ?? currentUser?.tenant_context?.gym_id
      ?? null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [currentUser?.gym_id, currentUser?.gymId, currentUser?.tenant_context?.gym_id]);
  const isTrainingClientForm = useMemo(
    () => isTrainingCoachClient(editingUser, formData),
    [editingUser, formData.trainer_id, formData.gym_id],
  );
  const [manageGymId, setManageGymId] = useState(() => {
    try {
      return sessionStorage.getItem('admin.users.d28dGymId') || '';
    } catch {
      return '';
    }
  });
  const [userGymFilterId, setUserGymFilterId] = useState('');
  const [createSedeParent, setCreateSedeParent] = useState(null);
  const [showCreateSedeParentPicker, setShowCreateSedeParentPicker] = useState(false);
  const [createSedePickerParentId, setCreateSedePickerParentId] = useState('');
  const [showCreateVirtualGymModal, setShowCreateVirtualGymModal] = useState(false);
  const gymScopeId = useMemo(() => {
    if (scopeGymId != null && scopeGymId !== '') {
      const fromProp = Number(scopeGymId);
      if (Number.isFinite(fromProp) && fromProp > 0) return fromProp;
    }
    if (isGymOperator && actorGymId) return actorGymId;
    if (isPlatformD28dActor && manageGymId) {
      const n = Number(manageGymId);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }, [scopeGymId, isGymOperator, actorGymId, isPlatformD28dActor, manageGymId]);
  const isGymScoped = gymScopeId != null && Number.isFinite(gymScopeId);
  const isGymSimpleForm = isGymScoped && !isSuperAdmin;
  const scopeGym = isGymScoped ? gymById[gymScopeId] : null;
  const isSedeScopeGym = Boolean(scopeGym?.parent_gym_id);
  const hidePlanForSede = isSedeActor || isSedeScopeGym;
  const sedeAudiencePlans = useMemo(() => {
    const assigned = Array.isArray(scopeGym?.available_plan_ids)
      ? scopeGym.available_plan_ids.map(String)
      : [];
    const allow = assigned.length ? new Set(assigned) : null;
    return (plans || []).filter((p) => {
      if (p.for_sedes !== true) return false;
      if (p.activo === false || p.visible === false) return false;
      if (String(p.kind || 'd28d') !== 'd28d') return false;
      if (!allow) return true;
      return allow.has(String(p.nombre)) || allow.has(String(p.display_nombre || ''));
    });
  }, [plans, scopeGym]);
  const [sedeCatalogPlans, setSedeCatalogPlans] = useState([]);
  const resolvedGymName = scopeGymName || scopeGym?.brand_name || scopeGym?.nombre || '';
  const canCreateChildSedes = isGymScoped
    && !scopeGym?.parent_gym_id
    && canCreateChildSedesUser({ roles: actorRoles, rol: actorRoles[0] }, scopeGym);
  const operationalGyms = useMemo(
    () => gyms.filter((g) => !isPlatformTemplateGym(g)),
    [gyms],
  );
  const rootVirtualGyms = useMemo(
    () => operationalGyms.filter((g) => g.parent_gym_id == null || g.parent_gym_id === ''),
    [operationalGyms],
  );
  const d28dPlatformGym = useMemo(
    () => operationalGyms.find(isD28dPlatformGym) || null,
    [operationalGyms],
  );
  /** Padres válidos para sede: ancla D28D + gyms virtuales (no SEED). */
  const d28dSedeParentCandidates = useMemo(
    () => rootVirtualGyms.filter((g) => !isPlatformTemplateGym(g)),
    [rootVirtualGyms],
  );
  const openPlatformCreateSede = useCallback(() => {
    const parents = d28dSedeParentCandidates;
    const platform = d28dPlatformGym;
    const wlVirtuals = parents.filter((g) => !isD28dPlatformGym(g));

    // Si está operando un gym virtual concreto, crear sede bajo ese.
    if (manageGymId) {
      const managed = parents.find((g) => String(g.id) === String(manageGymId));
      if (managed && !isD28dPlatformGym(managed)) {
        setCreateSedeParent(managed);
        return;
      }
    }

    if (!platform && wlVirtuals.length === 0) {
      setCreateSedePickerParentId('');
      setShowCreateSedeParentPicker(true);
      return;
    }

    // Más de un gym virtual WL → preguntar (D28D + virtuales).
    if (wlVirtuals.length > 1) {
      setCreateSedePickerParentId(String(platform?.id || wlVirtuals[0].id));
      setShowCreateSedeParentPicker(true);
      return;
    }

    // 0–1 virtual WL: sede bajo D28D (como AREA FIT / STRONGFIT en prod).
    if (platform) {
      setCreateSedeParent(platform);
      return;
    }
    setCreateSedeParent(wlVirtuals[0]);
  }, [d28dSedeParentCandidates, d28dPlatformGym, manageGymId]);
  const d28dFilterableGyms = useMemo(() => {
    // Incluye ancla D28D + gyms virtuales + sedes.
    return gymsForD28dAdminAssignment(operationalGyms);
  }, [operationalGyms]);
  const gymsTableRows = useMemo(() => {
    const q = searchGym.trim().toLowerCase();
    const match = (g) => {
      if (!q) return true;
      const haystack = [String(g.id), g.nombre, g.brand_name, g.ciudad, g.invite_code].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    };
    const rows = [];
    rootVirtualGyms.forEach((root) => {
      if (match(root)) rows.push({ gym: root, kind: 'virtual', depth: 0 });
      operationalGyms
        .filter((g) => Number(g.parent_gym_id) === Number(root.id) && match(g))
        .forEach((sede) => rows.push({ gym: sede, kind: 'sede', depth: 1, parent: root }));
    });
    operationalGyms
      .filter((g) => g.parent_gym_id != null && g.parent_gym_id !== ''
        && !rootVirtualGyms.some((r) => Number(r.id) === Number(g.parent_gym_id))
        && match(g))
      .forEach((orphan) => rows.push({ gym: orphan, kind: 'sede', depth: 0 }));
    return rows;
  }, [operationalGyms, rootVirtualGyms, searchGym]);
  const d28dGymsTableRows = useMemo(
    () => gymsTableRows.filter(({ gym }) => !isD28dPlatformGym(gym)),
    [gymsTableRows],
  );
  const pickVirtualGym = useCallback((gymId) => {
    const value = gymId ? String(gymId) : '';
    setManageGymId(value);
    try {
      if (value) sessionStorage.setItem('admin.users.d28dGymId', value);
      else sessionStorage.removeItem('admin.users.d28dGymId');
    } catch { /* ignore */ }
    if (value) {
      setUserGymFilterId(value);
    } else {
      setUserGymFilterId(''); // Todos
    }
    setViewTab(value ? 'users' : 'gyms');
  }, []);
  const showAssignmentsWorkspace = isSuperAdmin && scope === 'ecosystem-finals';
  const showDomainTabs = showAssignmentsWorkspace;
  const [foodRosterFilter, setFoodRosterFilter] = useState('linked');
  const [domainScope, setDomainScope] = useState(() => {
    if (!showDomainTabs) return scope;
    try {
      const pref = sessionStorage.getItem('admin.users.domainTab');
      if (pref && SUPER_ADMIN_DOMAIN_TABS.some((tab) => tab.id === pref)) return pref;
    } catch { /* ignore */ }
    // Usuarios con código de gym (p. ej. GYM-JABEL-005) viven en D28D, no en Ecosistema.
    return 'd28d';
  });
  const fitLiveTrainersTab = isFitLiveUiEnabled() && showDomainTabs && domainScope === 'd28d';

  useEffect(() => {
    if (!isSuperAdmin || scope !== 'ecosystem-finals') return;
    try {
      const pref = sessionStorage.getItem('admin.users.domainTab');
      if (pref && SUPER_ADMIN_DOMAIN_TABS.some((tab) => tab.id === pref)) {
        setDomainScope(pref);
        sessionStorage.removeItem('admin.users.domainTab');
      }
    } catch { /* ignore */ }
  }, [isSuperAdmin, scope]);

  useEffect(() => {
    if (isSuperAdmin && scope === 'ecosystem-finals') return;
    setDomainScope(scope);
  }, [scope, isSuperAdmin]);

  const effectiveScope = showDomainTabs ? domainScope : scope;
  /** Alta/edición de usuarios finales D28D: admin_d28d o super_admin en pestaña/dominio D28D. */
  const isD28dMemberAdminUi = Boolean(
    isPlatformD28dActor || effectiveScope === 'd28d' || scope === 'd28d',
  );
  const memberAssignableGyms = useMemo(() => {
    if (isD28dMemberAdminUi) {
      return gymsForD28dAdminAssignment(operationalGyms);
    }
    return gymsForMemberAssignment(
      operationalGyms,
      formData.gym_id || editingUser?.gym_id || editingUser?.gymId,
    );
  }, [isD28dMemberAdminUi, operationalGyms, formData.gym_id, editingUser]);

  const isTrainingUsersView = Boolean(
    isCoachActor
    || ['training-clients', 'coach-clients'].includes(effectiveScope),
  );
  /** Planes de activación por coach (mensual / bimestral / trimestral). */
  const coachTrainingPlans = useMemo(() => (
    (plans || []).filter((p) => (
      String(p.kind || '') === 'training'
      && p.activo !== false
      && p.visible !== false
      && String(p.training_audience || 'coach') === 'coach'
    )).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
  ), [plans]);
  // D28D/gym: columnas de vigencia. Training: mismas columnas estado/días (vista propia).
  const showSubscriptionCols = (
    (canManageSubscription && (isGymScoped || ['d28d', 'default', 'ecosystem-finals'].includes(effectiveScope)))
    || isTrainingUsersView
  );
  // Acciones de pago/activar D28D en tabla: no el coach (usa «Activar clientes») ni filas Training.
  const showSubActionButtons = canManageSubscription && !isCoachActor && !isTrainingUsersView;
  const isFoodClientsView = effectiveScope === 'food-clients';
  const tableColSpan = (showSubscriptionCols
    ? (isTrainingUsersView ? 12 : 10)
    : (isTrainingUsersView ? 10 : 8)) + (isFoodClientsView ? 1 : 0);

  const scopedUsers = useMemo(
    () => filterUsersByScope(users, isGymScoped ? 'default' : effectiveScope),
    [users, effectiveScope, isGymScoped],
  );

  const foodRosterStats = useMemo(
    () => (effectiveScope === 'food-clients' ? summarizeFoodRoster(scopedUsers) : null),
    [effectiveScope, scopedUsers],
  );

  const childSedes = useMemo(() => {
    if (!isGymScoped || !gymScopeId) return [];
    return gyms.filter((g) => Number(g.parent_gym_id) === Number(gymScopeId));
  }, [gyms, isGymScoped, gymScopeId]);

  const gymScopedUsers = useMemo(() => {
    if (!isGymScoped) return scopedUsers;
    return scopedUsers.filter((u) => {
      const gid = userGymId(u);
      if (gid !== gymScopeId) return false;
      if (isPlatformD28dActor && isPlatformStaffUser(u)) return false;
      return true;
    });
  }, [scopedUsers, isGymScoped, gymScopeId, isPlatformD28dActor]);

  const effectiveUsers = useMemo(() => {
    const base = isGymScoped ? gymScopedUsers : scopedUsers;
    const filterGymId = isGymScoped
      ? gymScopeId
      : (userGymFilterId ? Number(userGymFilterId) : null);

    const platformId = d28dPlatformGym?.id != null ? Number(d28dPlatformGym.id) : null;
    const isLiveHost = (u) => {
      const roles = rolesOfUser(u);
      return roles.includes('entrenador_d28d') || roles.includes('entrenador_gym');
    };
    const matchesGymOrHost = (u) => {
      const gid = userGymId(u);
      // admin_d28d sin filtro (Todos): ver todos los usuarios del alcance.
      if (isPlatformD28dActor && (filterGymId == null || !Number.isFinite(filterGymId))) {
        return true;
      }
      if (filterGymId != null && Number.isFinite(filterGymId)) {
        const filteringPlatform = platformId != null && filterGymId === platformId;
        if (gid === filterGymId) {
          // En sede / gym WL no mezclar hosts D28D aunque tengan gym_id de la sede.
          if (!filteringPlatform && isLiveHost(u) && rolesOfUser(u).includes('entrenador_d28d')) {
            return false;
          }
          return true;
        }
        // Hosts D28D sin gym_id (o ancla) solo cuando el filtro es la plataforma D28D.
        if (filteringPlatform && isLiveHost(u) && rolesOfUser(u).includes('entrenador_d28d')) {
          if (gid == null || gid === platformId) return true;
        }
        return false;
      }
      return true;
    };

    let rows = base.filter(matchesGymOrHost);

    // Coach: solo sus asesorados (refuerzo UI; el API ya filtra por trainer_id).
    if (isCoachActor && coachTrainerId != null && !hasAnyRole(['admin_training', 'admin_entrenador'])) {
      rows = rows.filter((u) => Number(u.trainer_id) === Number(coachTrainerId));
    }

    if (isPlatformD28dActor && viewTab === 'users') {
      rows = rows.filter((u) => {
        const roles = rolesOfUser(u);
        // Ocultar solo admins de plataforma; el resto (finales, admin_sede, hosts, etc.) sí.
        if (roles.includes('super_admin') || roles.includes('admin_d28d')) return false;
        if (filterGymId != null && Number.isFinite(filterGymId)) {
          const filteringPlatform = platformId != null && filterGymId === platformId;
          if (!filteringPlatform && isLiveHost(u) && roles.includes('entrenador_d28d')) {
            const gid = userGymId(u);
            if (gid == null || gid === platformId) return false;
          }
        }
        return true;
      });
    }

    if (effectiveScope === 'food-clients' && foodRosterFilter !== 'all') {
      rows = rows.filter((u) => {
        const linked = hasFoodNestLink(u);
        return foodRosterFilter === 'linked' ? linked : !linked;
      });
    }

    return rows;
  }, [
    isGymScoped,
    gymScopedUsers,
    scopedUsers,
    userGymFilterId,
    gymScopeId,
    isPlatformD28dActor,
    viewTab,
    d28dPlatformGym,
    effectiveScope,
    foodRosterFilter,
    isCoachActor,
    coachTrainerId,
    hasAnyRole,
  ]);

  const scopeMeta = isGymScoped
    ? {
      title: t('gym.masters.users_title', 'Usuarios de {gym}').replace('{gym}', resolvedGymName || t('gym.masters.your_gym', 'tu gimnasio')),
      subtitle: t('gym.masters.users_subtitle', 'Solo personas registradas en tu sede virtual.'),
    }
    : isPlatformD28dActor
      ? (viewTab === 'gyms'
        ? {
          title: t('users.gyms_d28d_title', 'Gimnasios virtuales y sedes'),
          subtitle: t('users.gyms_d28d_hint', 'Un solo ecosistema D28D: cada gym virtual puede tener sedes físicas con su propio código.'),
        }
        : {
          title: t('users.title_d28d', 'Usuarios D28D'),
          subtitle: t(
            'users.d28d_all_hint',
            'Todos los usuarios del ecosistema D28D (finales, sedes, hosts). Filtra por gimnasio si quieres acotar.',
          ),
        })
      : (getScopeMeta(effectiveScope) || getScopeMeta(scope) || SCOPE_META[effectiveScope] || SCOPE_META[scope] || null);
  const showScopedUserWorkspace = true;
  // admin_d28d usa scope d28d pero necesita pestañas Usuarios / Gimnasios en la misma vista
  const dedicatedScope = scope && scope !== 'default' && scope !== 'ecosystem-finals'
    && !(isPlatformD28dActor && scope === 'd28d');
  const showPlatformWorkspaceTabs = isPlatformD28dActor && !showDomainTabs;

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    const base = (q && showDomainTabs) ? users : effectiveUsers;
    const searched = !q ? base : base.filter((user) => {
      const roles = (user.roles?.length ? user.roles : [user.rol]).filter(Boolean);
      const gymName = userGymDisplayName(user, gymById, (key, fallback) => fallback);
      const trainerName = user.trainer_id ? (trainerById[Number(user.trainer_id)]?.nombre || '') : '';
      const plan = plans.find((p) => p.nombre === user.planId);
      const program = programs.find((p) => p.id === plan?.program_id);
      const promo = user.module_access?.training_promo;
      const haystack = [
        String(user.id),
        user.nombre,
        user.email,
        user.planId,
        gymName,
        trainerName,
        program?.name,
        promo?.code,
        promo?.label,
        ...roles,
        ...roles.map(formatRoleLabel),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
    if (!isTrainingUsersView || promoFilter !== 'promo') return searched;
    return searched.filter((user) => Boolean(user.module_access?.training_promo?.code));
  }, [effectiveUsers, users, showDomainTabs, userSearch, gymById, trainerById, plans, programs, isTrainingUsersView, promoFilter]);

  const sortedFilteredUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      const ka = subscriptionSortRank(a, gymById, gyms);
      const kb = subscriptionSortRank(b, gymById, gyms);
      if (ka.pending !== kb.pending) return ka.pending - kb.pending;
      return ka.days - kb.days;
    });
  }, [filteredUsers, gymById, gyms]);

  const filteredGyms = useMemo(() => operationalGyms.filter((g) => {
    const q = searchGym.trim().toLowerCase();
    if (!q) return true;
    const haystack = [String(g.id), g.nombre, g.brand_name, g.ciudad, g.invite_code].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(q);
  }), [operationalGyms, searchGym]);

  const filteredTrainers = useMemo(() => {
    const q = searchTrainer.trim().toLowerCase();
    if (!q) return trainers;
    return trainers.filter((tr) => {
      const haystack = [String(tr.id), tr.nombre, tr.email, tr.especialidad, tr.invite_code].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [trainers, searchTrainer]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data.data || []);
      setError('');
    } catch {
      setError(t('users.error_loading', 'Error cargando usuarios'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const downloadUsersExcel = useCallback(async (status) => {
    setExcelBusy(true);
    setExportStatusPrompt(false);
    try {
      const params = { status };
      if (isGymScoped && gymScopeId) params.gym_id = gymScopeId;
      else if (userGymFilterId) params.gym_id = userGymFilterId;
      const res = await api.get('/admin/users/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `usuarios_${status}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      emitToast({ type: 'success', title: t('users.export_ok', 'Excel de usuarios descargado') });
    } catch {
      emitToast({ type: 'error', title: t('users.export_error', 'No se pudo exportar usuarios') });
    } finally {
      setExcelBusy(false);
    }
  }, [isGymScoped, gymScopeId, userGymFilterId, t]);

  const downloadUsersImportTemplate = useCallback(async () => {
    setExcelBusy(true);
    try {
      const res = await api.get('/admin/users/import-template', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_usuarios.xlsx';
      a.click();
      URL.revokeObjectURL(url);
      emitToast({ type: 'success', title: t('users.template_ok', 'Plantilla descargada') });
    } catch {
      emitToast({ type: 'error', title: t('users.template_error', 'No se pudo descargar la plantilla') });
    } finally {
      setExcelBusy(false);
    }
  }, [t]);

  const runUsersImportExcel = useCallback(async (file) => {
    if (!file) return;
    setExcelBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (isGymScoped && gymScopeId) fd.append('gym_id', String(gymScopeId));
      else if (userGymFilterId) fd.append('gym_id', String(userGymFilterId));
      const defaultPlan = formData?.planId || '';
      if (defaultPlan) fd.append('planId', defaultPlan);
      if (formData?.cycle_id) fd.append('cycle_id', String(formData.cycle_id));
      const res = await api.post('/admin/users/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const report = res.data?.data || {};
      const errN = (report.errors || []).length;
      const mailErrN = (report.email_errors || []).length;
      const mailOk = report.emails_sent || 0;
      emitToast({
        type: errN || mailErrN ? 'warning' : 'success',
        title: t(
          'users.import_ok',
          `Importación: ${report.created || 0} creados, ${report.skipped_duplicate || 0} duplicados, ${errN} errores`,
        ),
        message: t(
          'users.import_mail_summary',
          `Correos enviados: ${mailOk}${mailErrN ? ` · fallos email: ${mailErrN}` : ''}${report.password_generated ? ` · contraseñas generadas (fila <8 chars): ${report.password_generated}` : ''}`,
        ),
      });
      await fetchUsers();
    } catch (e) {
      emitToast({
        type: 'error',
        title: e?.response?.data?.error || t('users.import_error', 'Error importando usuarios'),
      });
    } finally {
      setExcelBusy(false);
    }
  }, [isGymScoped, gymScopeId, userGymFilterId, formData?.planId, formData?.cycle_id, fetchUsers, t]);

  const fetchResources = useCallback(async () => {
    try {
      const planParams = isGymScoped && gymScopeId
        ? { gym_id: gymScopeId, visible: 'true' }
        : (effectiveScope === 'd28d' || domainScope === 'd28d'
          ? { platform_only: 'true', visible: 'true' }
          : { visible: 'true' });
      const progParams = isGymScoped && gymScopeId
        ? { gym_id: gymScopeId, gym_only: 'true' }
        : (effectiveScope === 'd28d' || domainScope === 'd28d' ? { platform_only: 'true' } : {});
      const cycleParams = isGymScoped && gymScopeId
        ? { gym_id: gymScopeId, gym_only: 'true' }
        : (effectiveScope === 'd28d' || domainScope === 'd28d' ? { platform_only: 'true' } : {});
      const requests = [
        api.get('/gyms'),
        api.get('/trainers'),
        api.get('/accounts/plans', { params: planParams }),
        api.get('/programs', { params: progParams }),
        api.get('/cycles', { params: cycleParams }),
      ];
      if (isGymScoped && gymScopeId) {
        requests.push(api.get(`/gyms/${gymScopeId}`));
      }
      const results = await Promise.all(requests);
      const [gymRes, trainerRes, plansRes, progRes, cyclesRes, scopeGymRes] = results;
      let gymList = gymRes.data || [];
      if (scopeGymRes?.data) {
        const detail = scopeGymRes.data;
        gymList = [...gymList.filter((g) => Number(g.id) !== Number(detail.id)), detail];
      }
      setGyms(dedupeGymsById(gymList));
      setTrainers(trainerRes.data || []);
      // Coach: hidratar ficha propia si el listado no trae invite_code.
      try {
        const actorTid = Number(currentUser?.trainer_id ?? currentUser?.trainerId);
        if (Number.isFinite(actorTid) && actorTid > 0) {
          const list = trainerRes.data || [];
          const mine = list.find((t) => Number(t.id) === actorTid);
          if (!mine?.invite_code) {
            const selfRes = await api.get(`/trainers/${actorTid}`).catch(() => null);
            if (selfRes?.data) {
              setTrainers((prev) => {
                const rest = (prev || []).filter((t) => Number(t.id) !== actorTid);
                return [...rest, selfRes.data];
              });
            }
          }
        }
      } catch {
        /* optional */
      }
      const plansData = Array.isArray(plansRes.data) ? plansRes.data : (plansRes.data.data || []);
      setPlans(plansData);
      setPrograms(progRes.data.data || []);
      setCycles(cyclesRes?.data?.data || cyclesRes?.data || []);
    } catch {
      console.error('Error loading resources');
    }
  }, [isGymScoped, gymScopeId, effectiveScope, domainScope, currentUser?.trainer_id, currentUser?.trainerId]);

  const saveGymInviteCode = useCallback(async (gymId, code) => {
    const { data } = await api.put(`/gyms/${gymId}`, { invite_code: code });
    const updated = data.gym || data;
    const savedCode = updated.invite_code || code;
    setGyms((prev) => prev.map((g) => (
      Number(g.id) === Number(gymId) ? { ...g, invite_code: savedCode } : g
    )));
    return savedCode;
  }, []);

  const generateGymInviteCode = useCallback(async (gymId) => {
    const { data } = await api.post(`/gyms/${gymId}/invite-code/generate`);
    const code = data.invite_code || data.gym?.invite_code || null;
    if (code) {
      setGyms((prev) => prev.map((g) => (Number(g.id) === Number(gymId) ? { ...g, invite_code: code } : g)));
    }
    return code;
  }, []);

  const inviteAutoGeneratedRef = useRef(new Set());

  useEffect(() => {
    fetchUsers();
    fetchResources();
  }, [fetchUsers, fetchResources]);

  useEffect(() => {
    if (!isPlatformD28dActor) return;
    try {
      const pref = sessionStorage.getItem('admin.users.d28dViewTab');
      if (pref === 'gyms') {
        sessionStorage.removeItem('admin.users.d28dViewTab');
        setViewTab('gyms');
      }
    } catch { /* ignore */ }
  }, [isPlatformD28dActor]);

  useEffect(() => {
    if (!isPlatformD28dActor || !manageGymId || !gyms.length) return;
    const id = Number(manageGymId);
    const exists = d28dFilterableGyms.some((g) => Number(g.id) === id);
    if (!exists) pickVirtualGym('');
  }, [isPlatformD28dActor, manageGymId, gyms, d28dFilterableGyms, pickVirtualGym]);

  useEffect(() => {
    if (!isPlatformD28dActor || isGymScoped || manageGymId) return;
    setUserGymFilterId((prev) => {
      if (prev === '') return prev;
      if (prev && d28dFilterableGyms.some((g) => String(g.id) === String(prev))) return prev;
      return ''; // Todos por defecto
    });
  }, [isPlatformD28dActor, isGymScoped, manageGymId, d28dFilterableGyms]);

  useEffect(() => {
    if (!editingUser || formData.editKind !== 'sede' || !formData.sede_gym_id) return undefined;
    let active = true;
    // admin_sede no puede listar planes del gym padre (403). Catálogo for_sedes de plataforma.
    api.get('/accounts/plans', {
      params: {
        kind: 'd28d',
        platform_only: 'true',
        include_for_sedes: 'true',
        visible: 'true',
      },
    })
      .then((res) => {
        if (!active) return;
        const plansData = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        if (plansData.length) setPlans(plansData);
      })
      .catch(() => { /* keep existing list */ });
    return () => { active = false; };
  }, [editingUser, formData.editKind, formData.sede_gym_id]);

  useEffect(() => {
    if (!showForm) return;
    const planParams = isGymScoped && gymScopeId
      ? { gym_id: gymScopeId, visible: 'true' }
      : (effectiveScope === 'd28d' || domainScope === 'd28d' ? { platform_only: 'true', visible: 'true' } : { visible: 'true' });
    api.get('/accounts/plans', { params: planParams })
      .then((plansRes) => {
        const plansData = Array.isArray(plansRes.data) ? plansRes.data : (plansRes.data?.data || []);
        setPlans(plansData);
      })
      .catch(() => { /* keep previous list */ });
  }, [showForm, isGymScoped, gymScopeId, effectiveScope, domainScope]);

  useEffect(() => {
    if (!isGymScoped || !gymScopeId) return;
    const gym = gymById[gymScopeId];
    if (!gym || gym.invite_code?.trim()) return;
    const key = String(gymScopeId);
    if (inviteAutoGeneratedRef.current.has(key)) return;
    inviteAutoGeneratedRef.current.add(key);
    generateGymInviteCode(gymScopeId).catch(() => {
      inviteAutoGeneratedRef.current.delete(key);
    });
  }, [isGymScoped, gymScopeId, gymById, generateGymInviteCode]);

  const saveTrainerInviteCode = async (trainerId, code) => {
    const { data } = await api.put(`/trainers/${trainerId}`, { invite_code: code });
    const updated = data.trainer || data;
    setTrainers((prev) => prev.map((t) => (t.id === trainerId ? { ...t, invite_code: updated.invite_code } : t)));
    return updated.invite_code;
  };

  const startEditTrainer = (tr) => {
    setEditingTrainer(tr);
    setTrainerEditForm({
      nombre: tr.nombre || '',
      email: tr.email || '',
      especialidad: tr.especialidad || '',
      capacidad_usuarios: tr.capacidad_usuarios ?? 50,
    });
    setError('');
  };

  const saveTrainerEdit = async (e) => {
    e.preventDefault();
    if (!editingTrainer?.id) return;
    setTrainerEditSaving(true);
    setError('');
    try {
      await api.put(`/trainers/${editingTrainer.id}`, {
        nombre: trainerEditForm.nombre.trim(),
        email: trainerEditForm.email.trim().toLowerCase(),
        especialidad: trainerEditForm.especialidad.trim() || null,
        capacidad_usuarios: Number(trainerEditForm.capacidad_usuarios) || 50,
      });
      setEditingTrainer(null);
      await fetchResources();
    } catch (err) {
      setError(err.response?.data?.error || t('trainers.save_error', 'Error al guardar entrenador'));
    } finally {
      setTrainerEditSaving(false);
    }
  };

  const handleDeleteTrainer = async (tr) => {
    if (!window.confirm(t('trainers.delete_confirm', '¿Eliminar al entrenador {name}?').replace('{name}', tr.nombre || ''))) {
      return;
    }
    try {
      await api.delete(`/trainers/${tr.id}`);
      await fetchResources();
    } catch (err) {
      setError(err.response?.data?.error || t('trainers.delete_error', 'Error al eliminar entrenador'));
    }
  };

  useEffect(() => {
    if (!isGymScoped || !gymScopeId || formData.gymCreateKind !== 'sede' || editingUser) return undefined;
    let active = true;
    api.get('/programs', { params: { gym_id: gymScopeId, gym_only: 'true' } })
      .then(async (res) => {
        if (!active) return;
        let list = res.data?.data || res.data || [];
        let arr = Array.isArray(list) ? list : [];
        if (!arr.length) {
          try {
            const platRes = await api.get('/programs', { params: { platform_only: 'true' } });
            list = platRes.data?.data || platRes.data || [];
            arr = Array.isArray(list) ? list.filter((p) => p.active !== false) : [];
          } catch { /* keep empty */ }
        }
        if (!active) return;
        setParentSedePrograms(arr);
        setFormData((prev) => ({
          ...prev,
          sede_calendar_program_ids: arr.map((p) => p.id),
        }));
      })
      .catch(() => {
        if (active) setParentSedePrograms([]);
      });
    return () => { active = false; };
  }, [isGymScoped, gymScopeId, formData.gymCreateKind, editingUser]);

  useEffect(() => {
    const editingSede = Boolean(editingUser && formData.editKind === 'sede');
    const creatingSede = Boolean(isGymScoped && formData.gymCreateKind === 'sede' && !editingUser);
    if (!isGymScoped || !gymScopeId || (!editingSede && !creatingSede)) return undefined;
    let active = true;
    Promise.all([
      api.get('/accounts/plans', {
        params: {
          kind: 'd28d',
          platform_only: 'true',
          include_for_sedes: 'true',
          visible: 'true',
        },
      }).catch(() => ({ data: [] })),
      api.get('/accounts/plans', {
        params: { kind: 'd28d', gym_id: gymScopeId, include_for_sedes: 'true', visible: 'true' },
      }).catch(() => ({ data: [] })),
    ]).then(([platRes, gymRes]) => {
      if (!active) return;
      const a = Array.isArray(platRes.data) ? platRes.data : (platRes.data?.data || []);
      const b = Array.isArray(gymRes.data) ? gymRes.data : (gymRes.data?.data || []);
      const map = new Map();
      [...a, ...b].forEach((p) => {
        if (p?.for_sedes === true && p.activo !== false && p.visible !== false) {
          map.set(p.nombre, p);
        }
      });
      const list = [...map.values()];
      setSedeCatalogPlans(list);
      if (creatingSede && list.length === 1) {
        setFormData((prev) => (prev.sede_plan_id ? prev : { ...prev, sede_plan_id: list[0].nombre }));
      }
    });
    return () => { active = false; };
  }, [isGymScoped, gymScopeId, formData.gymCreateKind, formData.editKind, editingUser]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'planId') {
        next.cycle_id = '';
        next.cycle_ids = [];
        const plan = plans.find((p) => p.nombre === value);
        if (isFoodCommercialPlan(plan) || isFoodCommercialPlan({ plan: value, planId: value })) {
          const alreadyFood = isFoodCommercialPlan({ plan: prev.planId, planId: prev.planId });
          if (!alreadyFood) {
            next.gym_id = '';
            next.module_access = foodOnlyModuleAccess();
          }
          if (!next.extend_vigencia_days) next.extend_vigencia_days = '30';
        }
      }
      // Editar sede: el email visible es login admin + contacto gym (sin campo separado).
      if (name === 'email' && prev.editKind === 'sede') {
        next.sede_contact_email = type === 'checkbox' ? prev.sede_contact_email : value;
      }
      return next;
    });
  };

  const createFormCycles = useMemo(() => {
    if (!formData.planId || !isD28dMemberAdminUi) return [];
    const plan = plans.find((p) => p.nombre === formData.planId);
    if (isFoodCommercialPlan(plan) || isFoodCommercialPlan({ plan: formData.planId })) return [];
    const allowed = Array.isArray(plan?.cycle_ids)
      ? plan.cycle_ids.map(Number).filter(Number.isFinite)
      : [];
    const pool = allowed.length
      ? cycles.filter((c) => allowed.includes(Number(c.id)))
      : cycles;
    return filterPurchasableCycles(pool);
  }, [formData.planId, plans, cycles, isD28dMemberAdminUi]);

  const createPlanCyclesIncluded = useMemo(() => {
    const plan = plans.find((p) => p.nombre === formData.planId);
    return planCyclesIncluded(plan);
  }, [plans, formData.planId]);

  const foodPlanSelected = useMemo(() => {
    const plan = plans.find((p) => p.nombre === formData.planId);
    return isFoodCommercialPlan(plan) || isFoodCommercialPlan({ plan: formData.planId, planId: formData.planId });
  }, [plans, formData.planId]);

  const createSelectedCycleIds = useMemo(() => {
    if (Array.isArray(formData.cycle_ids) && formData.cycle_ids.length) {
      return formData.cycle_ids.map(String);
    }
    if (!formData.cycle_id) return [];
    return cycleIdsFromPack(
      consecutiveCyclePack(createFormCycles, formData.cycle_id, createPlanCyclesIncluded),
    );
  }, [formData.cycle_ids, formData.cycle_id, createFormCycles, createPlanCyclesIncluded]);

  const pickCreateCyclePackStart = (startId) => {
    const pack = consecutiveCyclePack(createFormCycles, startId, createPlanCyclesIncluded);
    setFormData((prev) => ({
      ...prev,
      cycle_id: pack[0] ? String(pack[0].id) : String(startId || ''),
      cycle_ids: cycleIdsFromPack(pack),
    }));
  };

  useEffect(() => {
    if (!showForm || editingUser || !isD28dMemberAdminUi) return;
    if (!formData.planId || !createFormCycles.length) return;
    if (formData.cycle_id && createFormCycles.some((c) => String(c.id) === String(formData.cycle_id))) {
      if (createPlanCyclesIncluded > 1) {
        const pack = consecutiveCyclePack(createFormCycles, formData.cycle_id, createPlanCyclesIncluded);
        const ids = cycleIdsFromPack(pack);
        const prev = (formData.cycle_ids || []).map(String).join(',');
        if (ids.join(',') !== prev) {
          setFormData((p) => ({ ...p, cycle_ids: ids, cycle_id: ids[0] || p.cycle_id }));
        }
      }
      return;
    }
    const preferred = pickDefaultCycleId(createFormCycles);
    const start = preferred || String(createFormCycles[0].id);
    const pack = consecutiveCyclePack(createFormCycles, start, createPlanCyclesIncluded);
    setFormData((prev) => ({
      ...prev,
      cycle_id: pack[0] ? String(pack[0].id) : start,
      cycle_ids: cycleIdsFromPack(pack),
    }));
  }, [showForm, editingUser, isD28dMemberAdminUi, formData.planId, formData.cycle_id, formData.cycle_ids, createFormCycles, createPlanCyclesIncluded]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!editingUser && isGymSimpleForm && formData.gymCreateKind === 'sede') {
        const contactEmail = formData.email?.trim();
        if (!formData.nombre?.trim() || !contactEmail) {
          setError(t('users.sede_fields_required', 'Nombre y email de contacto son obligatorios para la sede'));
          return;
        }
        if (!formData.sede_plan_id) {
          setError(t('orgs.sede_plan_required', 'Debes asignar un plan de sede. Será el único plan visible para los usuarios de esta sede.'));
          return;
        }
        const payload = {
          nombre: formData.nombre.trim(),
          ciudad: formData.sede_ciudad?.trim() || scopeGym?.ciudad || '—',
          email: contactEmail,
          brand_name: formData.sede_brand_name?.trim() || formData.nombre.trim(),
          logo_url: formData.sede_logo_url?.trim() || undefined,
          primary_color: formData.sede_primary_color || '#2563eb',
          secondary_color: formData.sede_secondary_color || '#10b981',
          support_whatsapp: formData.sede_support_whatsapp?.trim() || undefined,
          welcome_message: formData.sede_welcome_message?.trim() || undefined,
          pais: 'Colombia',
          white_label_enabled: true,
          status: 'active',
          parent_gym_id: Number(gymScopeId),
          admin_email: formData.sede_admin_email?.trim() || contactEmail,
          admin_password: formData.sede_admin_password?.trim() || formData.password?.trim() || undefined,
          calendar_program_ids: formData.sede_calendar_program_ids?.length
            ? formData.sede_calendar_program_ids
            : parentSedePrograms.map((p) => p.id),
          welcome_videos_by_program: (() => {
            const raw = formData.sede_welcome_videos_by_program || {};
            const allowedIds = formData.sede_calendar_program_ids?.length
              ? formData.sede_calendar_program_ids
              : parentSedePrograms.map((p) => p.id);
            const allowed = new Set(allowedIds.map(String));
            const next = {};
            for (const [pid, url] of Object.entries(raw)) {
              if (!allowed.has(String(pid))) continue;
              const trimmed = String(url || '').trim();
              if (trimmed) next[String(pid)] = trimmed;
            }
            return next;
          })(),
          member_fee_cop: Math.max(0, Number(formData.sede_member_fee_cop) || 0),
          skip_member_payment: true,
          allow_food_addon: formData.sede_allow_food_addon !== false,
          available_plan_ids: [formData.sede_plan_id],
          plan_id: formData.sede_plan_id,
        };
        const { data } = await api.post('/gyms', payload);
        setEditingUser(null);
        setFormData(emptyForm());
        setShowForm(false);
        setError('');
        fetchResources();
        fetchUsers();
        if (data?.admin_credentials || data?.invite_code) {
          setSedeProvisionResult({
            title: t('orgs.create_sede_success', 'Sede creada'),
            inviteCode: data.invite_code || data.gym?.invite_code,
            adminCredentials: data.admin_credentials,
          });
        } else {
          emitToast({ type: 'success', title: t('orgs.create_sede_success', 'Sede creada') });
        }
        return;
      }

      if (editingUser) {
        setEditSaving(true);
        if (formData.editKind === 'sede' && formData.sede_gym_id) {
          const sedeGymId = Number(formData.sede_gym_id);
          // Contacto gym = email de login del admin (no hay campo UI aparte al editar).
          const sedeContactEmail = (
            formData.sede_contact_email?.trim()
            || formData.email?.trim()
            || ''
          ).toLowerCase() || undefined;
          // Perfil primero: si falla el email, no desincronizar el contacto del gym.
          if (formData.nombre?.trim() !== editingUser.nombre || formData.email?.trim() !== editingUser.email) {
            await api.put(`/admin/users/${editingUser.id}/profile`, {
              nombre: formData.nombre?.trim(),
              email: formData.email?.trim(),
            });
          }
          if (formData.sede_admin_password?.trim()) {
            await api.put(`/admin/users/${editingUser.id}/password`, {
              password: formData.sede_admin_password.trim(),
            });
          }
          const gymRes = await api.put(`/gyms/${sedeGymId}`, {
            nombre: formData.sede_nombre?.trim() || formData.nombre?.trim(),
            email: sedeContactEmail,
            ciudad: formData.sede_ciudad?.trim() || undefined,
            invite_code: formData.sede_invite_code?.trim() || undefined,
            calendar_program_ids: formData.sede_calendar_program_ids?.length
              ? formData.sede_calendar_program_ids
              : undefined,
            welcome_videos_by_program: (() => {
              const raw = formData.sede_welcome_videos_by_program || {};
              const allowed = new Set((formData.sede_calendar_program_ids || []).map(String));
              const next = {};
              for (const [pid, url] of Object.entries(raw)) {
                if (!allowed.has(String(pid))) continue;
                const trimmed = String(url || '').trim();
                if (trimmed) next[String(pid)] = trimmed;
              }
              return next;
            })(),
            member_fee_cop: Math.max(0, Number(formData.sede_member_fee_cop) || 0),
            skip_member_payment: formData.sede_skip_member_payment === true,
            allow_food_addon: formData.sede_allow_food_addon !== false,
            available_plan_ids: formData.sede_plan_id ? [formData.sede_plan_id] : [],
            plan_id: formData.sede_plan_id || undefined,
            brand_name: formData.sede_brand_name?.trim() || undefined,
            logo_url: formData.sede_logo_url?.trim() || undefined,
            primary_color: formData.sede_primary_color || undefined,
            secondary_color: formData.sede_secondary_color || undefined,
            support_whatsapp: formData.sede_support_whatsapp?.trim() || undefined,
            welcome_message: formData.sede_welcome_message?.trim() || undefined,
          });
          const updatedSedeGym = gymRes.data?.gym || gymRes.data;
          if (updatedSedeGym?.id) {
            setGyms((prev) => prev.map((g) => (
              Number(g.id) === Number(updatedSedeGym.id) ? { ...g, ...updatedSedeGym } : g
            )));
          }
          const alreadySedeAdmin = userRolesList(editingUser).includes('admin_sede');
          if (!alreadySedeAdmin) {
            const sedeAdminAccess = {
              gym: true,
              d28d: true,
              live_classes: true,
              ...(editingUser.module_access || {}),
            };
            await api.put(`/admin/users/${editingUser.id}/role`, {
              rol: 'admin_sede',
              roles: ['admin_sede'],
              module_access: sedeAdminAccess,
            });
          }
          // No re-asignar gym si ya está en la sede: en prod el PUT /assign
          // redundante devolvía 502 y tumaba un guardado de email/marca que ya había OK.
          const alreadyOnSedeGym = Number(editingUser.gym_id ?? editingUser.gymId) === Number(sedeGymId);
          if (!alreadyOnSedeGym) {
            await api.put(`/admin/users/${editingUser.id}/assign`, {
              gym_id: sedeGymId,
              trainer_id: null,
            });
          }
          const planChanged = formData.planId && formData.planId !== (editingUser.planId || editingUser.subscription?.plan || '');
          if (planChanged) {
            await api.put(`/admin/users/${editingUser.id}/assign`, {
              gym_id: sedeGymId,
              planId: formData.planId,
            });
          }
          const extendDays = Number(formData.extend_vigencia_days);
          const isSedeOpAdmin = isSedeOperatorUser(editingUser, gymById, gyms) || formData.editKind === 'sede';
          const shouldActivate = editingUser.subscription?.has_account
            && (editingUser.subscription?.pending || !editingUser.subscription?.active);
          const needsNewAccount = !editingUser.subscription?.has_account && Boolean(formData.planId);
          const needsOperatorActivation = isSedeOpAdmin
            && !editingUser.subscription?.has_account
            && !editingUser.subscription?.active
            && !editingUser.subscription?.operator_license;
          const activationDays = Number.isFinite(extendDays) && extendDays >= 1 && extendDays <= 365
            ? extendDays
            : 30;
          if (shouldActivate || needsNewAccount || needsOperatorActivation) {
            await api.post(`/payment-admin/users/${editingUser.id}/activate`, {
              days: activationDays,
              module_code: 'd28d',
              plan: formData.planId || editingUser.subscription?.plan || editingUser.planId || undefined,
              ...vigenciaPayloadFromForm(formData, activationDays),
            });
          } else if (Number.isFinite(extendDays) && extendDays >= 1 && extendDays <= 365
            && editingUser.subscription?.active) {
            await api.post(`/payment-admin/extend/${editingUser.id}`, {
              module_code: 'd28d',
              days: extendDays,
            });
          }
        } else if (isCoachActor) {
          if (formData.nombre?.trim() !== editingUser.nombre || formData.email?.trim() !== editingUser.email) {
            await api.put(`/admin/users/${editingUser.id}/profile`, {
              nombre: formData.nombre?.trim(),
              email: formData.email?.trim(),
            });
          }
          const prevPlan = editingUser.planId || editingUser.subscription?.plan || '';
          if (formData.planId && formData.planId !== prevPlan) {
            await api.put(`/admin/users/${editingUser.id}/assign`, {
              planId: formData.planId,
              trainer_id: coachTrainerId || editingUser.trainer_id || null,
            });
          }
        } else if (isGymSimpleForm && (formData.editKind === 'final_user'
          || userRolesList(editingUser).some((r) => ['usuario_final', 'usuario_final_sede'].includes(r)))) {
          await api.put(`/admin/users/${editingUser.id}/assign`, {
            gym_id: Number(gymScopeId),
            trainer_id: formData.trainer_id || null,
            planId: formData.planId || undefined,
            ...(formData.cycle_id ? { cycle_id: Number(formData.cycle_id) } : {}),
          });
          if (formData.nombre?.trim() !== editingUser.nombre || formData.email?.trim() !== editingUser.email) {
            await api.put(`/admin/users/${editingUser.id}/profile`, {
              nombre: formData.nombre?.trim(),
              email: formData.email?.trim(),
            });
          }
          const extendDays = resolveActivationDays(formData, editingUser);
          if (extendDays != null && userRolesList(editingUser).some((r) => ['usuario_final', 'usuario_final_sede'].includes(r))) {
            if (editingUser.subscription?.has_account) {
              if (editingUser.subscription?.pending || !editingUser.subscription?.active) {
                await api.post(`/payment-admin/users/${editingUser.id}/activate`, {
                  days: extendDays,
                  module_code: foodPlanSelected ? 'food' : 'd28d',
                  plan: formData.planId || editingUser.subscription?.plan || editingUser.planId || undefined,
                  ...(formData.cycle_id && !foodPlanSelected ? { cycle_id: Number(formData.cycle_id) } : {}),
                  ...vigenciaPayloadFromForm(formData, extendDays),
                });
              } else {
                await api.post(`/payment-admin/extend/${editingUser.id}`, {
                  module_code: foodPlanSelected ? 'food' : 'd28d',
                  days: extendDays,
                });
              }
            } else if (formData.planId) {
              await api.post(`/payment-admin/users/${editingUser.id}/activate`, {
                days: extendDays,
                module_code: foodPlanSelected ? 'food' : 'd28d',
                plan: formData.planId,
                ...(formData.cycle_id && !foodPlanSelected ? { cycle_id: Number(formData.cycle_id) } : {}),
                ...vigenciaPayloadFromForm(formData, extendDays),
              });
            }
          }
        } else if (isGymSimpleForm && !userRolesList(editingUser).some((r) => ['usuario_final', 'usuario_final_sede'].includes(r))) {
          if (formData.nombre?.trim() !== editingUser.nombre || formData.email?.trim() !== editingUser.email) {
            await api.put(`/admin/users/${editingUser.id}/profile`, {
              nombre: formData.nombre?.trim(),
              email: formData.email?.trim(),
            });
          }
        } else {
        const normalizedAccess = { ...EMPTY_MODULE_ACCESS, ...formData.module_access };
        normalizedAccess.nutrition = normalizedAccess.food_plan === true;
        const selectedPlan = plans.find((p) => p.nombre === formData.planId);
        if (formData.d28d_program) {
          normalizedAccess.d28d_program = String(formData.d28d_program);
        } else if (selectedPlan?.program_id) {
          normalizedAccess.d28d_program = String(selectedPlan.program_id);
        }
        if (isD28dMemberAdminUi && formData.planId && !formData.cycle_id) {
          const existingCycle = editingUser?.subscription?.cycle_id;
          const selectedPlanRow = plans.find((p) => p.nombre === formData.planId);
          const foodEdit = isFoodCommercialPlan(selectedPlanRow) || isFoodCommercialPlan({ plan: formData.planId });
          const trainingEdit = isTrainingCoachClient(editingUser, formData);
          if (!foodEdit && !trainingEdit) {
            const allowed = Array.isArray(selectedPlanRow?.cycle_ids) ? selectedPlanRow.cycle_ids : [];
            const pool = allowed.length
              ? cycles.filter((c) => allowed.map(Number).includes(Number(c.id)))
              : cycles;
            const effectivePool = (allowed.length && !pool.length) ? cycles : pool;
            const planHasCycles = filterPurchasableCycles(effectivePool).length > 0;
            if (planHasCycles && existingCycle == null) {
              setError(t('users.cycle_required', 'Selecciona el ciclo de activación'));
              setEditSaving(false);
              return;
            }
          }
        }
        const foodPlanOnUser = isFoodCommercialPlan(selectedPlan) || isFoodCommercialPlan({ plan: formData.planId });
        const wantsD28d = isSuperAdmin && wantsD28dProductAccess(normalizedAccess);
        const trainingAssign = isTrainingCoachClient(editingUser, formData) && !wantsD28d;
        const foodAssign = foodPlanOnUser && !wantsD28d;
        if (foodAssign) {
          Object.assign(normalizedAccess, foodOnlyModuleAccess());
          delete normalizedAccess.d28d_program;
        }
        if (trainingAssign) {
          normalizedAccess.d28d = false;
          normalizedAccess.gym = false;
          normalizedAccess.live_classes = false;
          delete normalizedAccess.d28d_program;
          normalizedAccess.training = true;
        }
        if (foodPlanOnUser && wantsD28d) {
          normalizedAccess.food_plan = true;
          normalizedAccess.nutrition = true;
        }
        const assignGymId = (foodAssign || trainingAssign)
          ? null
          : (isGymScoped
            ? (userGymId(editingUser) || Number(gymScopeId))
            : (formData.gym_id || userGymId(editingUser) || null));
        const assignCycleId = (foodPlanOnUser || trainingAssign)
          ? ''
          : (formData.cycle_id
            || (editingUser?.subscription?.cycle_id != null ? String(editingUser.subscription.cycle_id) : ''));
        const assignRes = await api.put(`/admin/users/${editingUser.id}/assign`, {
          ...(assignGymId != null && assignGymId !== ''
            ? { gym_id: Number(assignGymId) }
            : (trainingAssign ? { gym_id: null } : {})),
          trainer_id: (foodAssign && !wantsD28d)
            ? null
            : (trainingAssign
              ? (formData.trainer_id || editingUser.trainer_id || null)
              : (isD28dMemberAdminUi ? null : (formData.trainer_id || null))),
          planId: formData.planId || undefined,
          ...(assignCycleId ? { cycle_id: Number(assignCycleId) } : {}),
          ...(wantsD28d ? { grant_d28d: true, module_access: normalizedAccess } : {}),
        });
        const savedGymId = assignRes?.data?.data?.gym_id;
        if (assignGymId != null && assignGymId !== ''
          && savedGymId != null
          && Number(savedGymId) !== Number(assignGymId)) {
          setError(t('users.gym_save_failed', 'No se pudo guardar el gimnasio. Intenta de nuevo.'));
          setEditSaving(false);
          return;
        }
        const savedCycleId = assignRes?.data?.data?.cycle_id;
        if (formData.cycle_id && !foodPlanOnUser && !trainingAssign && (savedCycleId == null || savedCycleId === '')) {
          setError(t('users.cycle_save_failed', 'No se pudo guardar el ciclo de activación. Intenta de nuevo.'));
          setEditSaving(false);
          return;
        }
        if (formData.cycle_id && !foodPlanOnUser && savedCycleId != null) {
          emitToast({
            type: 'success',
            title: t('users.cycle_saved_title', 'Ciclo guardado'),
            message: t('users.cycle_saved_msg', 'Ciclo de activación actualizado (id {id}).').replace('{id}', String(savedCycleId)),
          });
        }
        if (isSuperAdmin || (isPlatformD28dActor && isD28dDomainUser(editingUser))) {
          await api.put(`/admin/users/${editingUser.id}/role`, {
            roles: formData.roles,
            module_access: normalizedAccess,
          });
        }
        if (formData.nombre?.trim() !== editingUser.nombre
          || formData.email?.trim() !== editingUser.email
          || String(formData.telefono || '').trim() !== String(editingUser.telefono || '').trim()) {
          await api.put(`/admin/users/${editingUser.id}/profile`, {
            nombre: formData.nombre?.trim(),
            email: formData.email?.trim(),
            telefono: formData.telefono?.trim() || null,
          });
        }
        const extendDays = resolveActivationDays(formData, editingUser);
        const isTrainingClient = isTrainingCoachClient(editingUser, formData);
        if (wantsD28d && userRolesList(editingUser).includes('usuario_final')) {
          const addonDaysRaw = Number(formData.extend_vigencia_days);
          const d28dDays = Number.isFinite(addonDaysRaw) && addonDaysRaw >= 1 && addonDaysRaw <= 365
            ? addonDaysRaw
            : 30;
          const d28dPlan = String(formData.d28d_addon_plan || '').trim() || undefined;
          if (!editingUser.subscription?.has_d28d_product) {
            await api.post(`/payment-admin/users/${editingUser.id}/activate`, {
              days: d28dDays,
              module_code: 'd28d',
              grant_d28d: true,
              ...(d28dPlan ? { plan: d28dPlan, d28d_plan: d28dPlan } : {}),
              ...(formData.cycle_id ? { cycle_id: Number(formData.cycle_id) } : {}),
            });
          } else if (Number.isFinite(addonDaysRaw) && addonDaysRaw >= 1 && addonDaysRaw <= 365) {
            await api.post(`/payment-admin/extend/${editingUser.id}`, {
              module_code: 'd28d',
              days: addonDaysRaw,
            });
          }
        } else if (extendDays != null && userRolesList(editingUser).includes('usuario_final')) {
          if (editingUser.subscription?.has_account) {
            if (editingUser.subscription?.pending || !editingUser.subscription?.active) {
              await activateUserSubscription(editingUser, {
                days: extendDays,
                plan: formData.planId || editingUser.subscription?.plan || editingUser.planId || undefined,
                cycle_id: formData.cycle_id ? Number(formData.cycle_id) : undefined,
                formData,
                editingUser,
              });
            } else if (!isTrainingClient) {
              await api.post(`/payment-admin/extend/${editingUser.id}`, {
                module_code: foodPlanSelected ? 'food' : 'd28d',
                days: extendDays,
              });
            }
          } else if (isTrainingClient) {
            await activateUserSubscription(editingUser, {
              days: extendDays,
              plan: formData.planId || editingUser.subscription?.plan || editingUser.planId || undefined,
              cycle_id: formData.cycle_id ? Number(formData.cycle_id) : undefined,
              formData,
              editingUser,
              gymById,
              gyms,
            });
          } else if (formData.planId && !isTrainingClient) {
            await activateUserSubscription(editingUser, {
              days: extendDays,
              plan: formData.planId,
              cycle_id: formData.cycle_id ? Number(formData.cycle_id) : undefined,
              formData,
              editingUser,
            });
          }
        }
        }
      } else {
        // Validaciones obligatorias para nuevo usuario
        const needsPlan = (!isCoachActor
          && formData.roles.includes('usuario_final')
          && !formData.roles.includes('entrenador')
          && (!hidePlanForSede || sedeAudiencePlans.length > 0))
          || (isCoachActor && coachTrainingPlans.length > 0);
        if (needsPlan && !formData.planId) {
          setError(t('plans.subscription', 'Plan de Suscripción') + ' ' + t('common.name_required', 'Nombre *').replace('Nombre *','es obligatorio'));
          return;
        }
        if (isD28dMemberAdminUi && !foodPlanSelected && formData.planId && createFormCycles.length > 0 && !formData.cycle_id) {
          setError(t('users.cycle_required', 'Selecciona el ciclo de activación'));
          return;
        }
        const sedeCreateDays = Number(formData.extend_vigencia_days);
        if ((isSedeActor || isSedeScopeGym) && formData.gymCreateKind === 'usuario_final') {
          if (!Number.isFinite(sedeCreateDays) || sedeCreateDays < 1 || sedeCreateDays > 365) {
            setError(t('users.days_active_required', 'Indica cuántos días estará activo el usuario (1–365).'));
            return;
          }
        }
        if (!isCoachActor && formData.roles.includes('usuario_final') && !formData.gym_id && !isGymScoped && !foodPlanSelected) {
          setError(t('common.gym', 'Gimnasio') + ' ' + t('common.name_required', 'Nombre *').replace('Nombre *','es obligatorio') + ' ' + '(Usuario Final)');
          return;
        }
        // Validar email duplicado en cliente
        const existingUser = users.find(
          (u) => String(u.email).toLowerCase() === String(formData.email).toLowerCase(),
        );
        if (existingUser) {
          const tabHint = isFoodDomainUser(existingUser) || isFoodOnlyDomainUser(existingUser)
            ? 'FOOD'
            : (isTrainingDomainUser(existingUser) ? 'Clientes Training' : (isD28dDomainUser(existingUser) ? 'D28D' : 'otra pestaña'));
          setError(
            t(
              'users.email_exists_other_tab',
              'Este email ya está registrado ({name}). Búscalo en la pestaña {tab}.',
            ).replace('{name}', existingUser.nombre || formData.email).replace('{tab}', tabHint),
          );
          setUserSearch(String(formData.email).trim());
          if (showDomainTabs) {
            if (isFoodDomainUser(existingUser) || isFoodOnlyDomainUser(existingUser)) setDomainScope('food-clients');
            else if (isTrainingDomainUser(existingUser)) setDomainScope('training-clients');
            else if (isD28dDomainUser(existingUser)) setDomainScope('d28d');
          }
          return;
        }
        // Create logic
        const moduleAccess = foodPlanSelected
          ? foodOnlyModuleAccess()
          : (isGymSimpleForm
            ? { ...GYM_FINAL_USER_MODULE_ACCESS }
            : (isCoachActor ? { ...COACH_CREATE_MODULE_ACCESS } : formData.module_access));
        const isSedeScope = Boolean(scopeGym?.parent_gym_id);
        const finalConsumerRole = isSedeScope ? 'usuario_final_sede' : 'usuario_final';
        await api.post('/admin/users', {
          ...formData,
          telefono: formData.telefono?.trim() || null,
          rol: isGymSimpleForm ? finalConsumerRole : (isCoachActor ? 'usuario_final' : (formData.roles[0] || 'usuario_final')),
          roles: isGymSimpleForm ? [finalConsumerRole] : (isCoachActor ? ['usuario_final'] : formData.roles),
          gym_id: foodPlanSelected
            ? null
            : (isGymScoped
            ? Number(gymScopeId)
            : (isCoachActor ? null : (formData.roles.includes('usuario_final') && formData.gym_id ? parseInt(formData.gym_id, 10) : undefined))),
          gymId: foodPlanSelected
            ? null
            : (isGymScoped
            ? Number(gymScopeId)
            : (isCoachActor ? null : (formData.roles.includes('usuario_final') && formData.gym_id ? parseInt(formData.gym_id, 10) : undefined))),
          trainer_id: isD28dMemberAdminUi
            ? null
            : (isCoachActor
              ? (coachTrainerId || currentUser?.trainer_id || formData.trainer_id || null)
              : (formData.trainer_id || null)),
          planId: formData.roles.includes('entrenador')
            ? null
            : (isCoachActor ? (formData.planId || null) : formData.planId),
          cycle_id: foodPlanSelected
            ? undefined
            : (isD28dMemberAdminUi && formData.cycle_id
            ? Number(formData.cycle_id)
            : undefined),
          module_access: moduleAccess,
          copy_gallery_from_nico: formData.roles.includes('entrenador') && formData.copy_gallery_from_nico !== false,
          copy_routines_from_nico: formData.roles.includes('entrenador') && formData.copy_routines_from_nico !== false,
        }).then(async (res) => {
          const tempPwd = res.data?.temporary_password;
          const createdId = res.data?.data?.id;
          if ((isSedeActor || isSedeScopeGym) && createdId) {
            const days = Number(formData.extend_vigencia_days) || 30;
            try {
              await api.post(`/payment-admin/users/${createdId}/activate`, {
                days,
                module_code: 'd28d',
                plan: formData.planId || undefined,
                force_enable: true,
                ...(formData.cycle_id ? { cycle_id: Number(formData.cycle_id) } : {}),
              });
              emitToast({
                type: 'success',
                title: t('users.created_active', 'Usuario creado y activado'),
                message: t(
                  'users.created_active_msg',
                  '{email} quedó activo por {n} días.',
                  { email: formData.email, n: String(days) },
                ),
                duration: 12000,
              });
              try {
                window.dispatchEvent(new CustomEvent('d28d:notifications:refresh'));
              } catch { /* ignore */ }
            } catch (actErr) {
              emitToast({
                type: 'error',
                title: t('users.created', 'Usuario creado'),
                message: actErr?.response?.data?.error
                  || t('users.created_pending_activate', 'Usuario creado, pero no se pudo activar automáticamente. Actívalo en Vigencias.'),
                duration: 12000,
              });
            }
          }
          if (foodPlanSelected && createdId && !(isSedeActor || isSedeScopeGym)) {
            const days = Number(formData.extend_vigencia_days) || 30;
            try {
              await api.post(`/payment-admin/users/${createdId}/activate`, {
                days,
                module_code: 'food',
                plan: formData.planId || undefined,
                force_enable: true,
              });
            } catch (actErr) {
              emitToast({
                type: 'error',
                title: t('users.created', 'Usuario creado'),
                message: actErr?.response?.data?.error
                  || t('users.created_pending_activate', 'Usuario creado, pero no se pudo activar automáticamente. Actívalo en Vigencias.'),
                duration: 12000,
              });
            }
          }
          if (tempPwd) {
            emitToast({
              type: 'success',
              title: t('users.created', 'Usuario creado'),
              message: t(
                'users.temp_password_hint',
                'Contraseña temporal para {email}: {password}',
                { email: formData.email, password: tempPwd },
              ),
              duration: 20000,
            });
          }
          return res;
        });
      }
      if (editingUser) {
        const healthChanged = Boolean(formData.tiene_restricciones) !== Boolean(editingUser.tiene_restricciones)
          || (formData.restricciones_detalles || '') !== (editingUser.restricciones_detalles || '')
          || Boolean(formData.soy_mama_posparto) !== isPostpartumMom(editingUser);
        if (healthChanged) {
          await api.put(`/admin/users/${editingUser.id}/profile`, {
            tiene_restricciones: Boolean(formData.tiene_restricciones),
            restricciones_detalles: formData.restricciones_detalles || '',
            soy_mama_posparto: Boolean(formData.soy_mama_posparto),
          });
        }
      }
      setEditingUser(null);
      setFormData(emptyForm());
      setShowForm(false);
      setEditSaving(false);
      fetchUsers();
      fetchResources();
    } catch (err) {
      setEditSaving(false);
      const status = err?.response?.status;
      const apiMsg = err?.response?.data?.error;
      const msg = apiMsg
        || (status === 502 || status === 503
          ? t('users.save_gateway_error', 'El servidor no respondió (502). Reintenta en unos segundos; si persiste, revisa shell-backend.')
          : t('users.save_error', 'Error al guardar'));
      setError(msg);
    }
  };

  const startEdit = async (user, { preferSedeForm = null } = {}) => {
    setEditingUser(user);
    setShowForm(false);
    setError('');
    setEditLoading(true);
    let module_access = { ...EMPTY_MODULE_ACCESS, ...(user.module_access || {}) };
    try {
      const licRes = await api.get(`/licenses/user/${user.id}`);
      const resolved = licRes.data?.data?.module_access;
      if (resolved && typeof resolved === 'object') {
        module_access = { ...EMPTY_MODULE_ACCESS, ...resolved };
      }
    } catch {
      /* fallback module_access del listado */
    }

    const trainingEdit = isTrainingCoachClient(user, {});
    const baseForm = {
      nombre: user.nombre,
      email: user.email,
      password: '',
      telefono: user.telefono || '',
      roles: userRolesList(user),
      gym_id: trainingEdit ? '' : (user.gym_id ?? user.gymId ?? (isGymScoped ? String(gymScopeId) : '')),
      trainer_id: user.trainer_id || '',
      planId: user.planId || user.subscription?.plan || '',
      cycle_id: user.subscription?.cycle_id != null ? String(user.subscription.cycle_id) : '',
      cycle_ids: user.subscription?.cycle_id != null ? [String(user.subscription.cycle_id)] : [],
      d28d_program: module_access.d28d_program || '',
      d28d_addon_plan: '',
      module_access,
      editKind: 'user',
      sede_gym_id: '',
      sede_nombre: '',
      sede_contact_email: '',
      sede_invite_code: '',
      sede_ciudad: '',
      sede_admin_password: '',
      sede_brand_name: '',
      sede_logo_url: '',
      sede_primary_color: '#2563eb',
      sede_secondary_color: '#10b981',
      sede_support_whatsapp: '',
      sede_welcome_message: '',
      sede_calendar_program_ids: [],
      sede_welcome_videos_by_program: {},
      sede_member_fee_cop: '',
      sede_skip_member_payment: true,
      sede_allow_food_addon: true,
      extend_vigencia_days: (user.subscription?.has_account || user.subscription?.operator_license) && user.subscription?.active
        ? ''
        : '30',
      tiene_restricciones: Boolean(user.tiene_restricciones),
      restricciones_detalles: user.restricciones_detalles || '',
      soy_mama_posparto: isPostpartumMom(user),
      ...resolveVigenciaFormDefaults(
        user,
        (user.subscription?.has_account || user.subscription?.operator_license) && user.subscription?.active
          ? (user.subscription?.days_left || 30)
          : 30,
      ),
      gymCreateKind: 'usuario_final',
    };

    // Usuario de gym virtual: catálogo del gym (+ espejo plataforma) y remapear cycle_id por fecha.
    const editGymId = userGymId(user);
    if (editGymId != null && Number.isFinite(Number(editGymId)) && !isGymScoped) {
      try {
        const [cycleRes, platRes] = await Promise.all([
          api.get('/cycles', { params: { gym_id: editGymId, gym_only: 'true' } }),
          api.get('/cycles', { params: { platform_only: 'true' } }).catch(() => null),
        ]);
        const gymCycles = cycleRes?.data?.data || cycleRes?.data || [];
        const platCycles = platRes?.data?.data || platRes?.data || [];
        if (Array.isArray(gymCycles) && gymCycles.length) {
          setCycles(gymCycles);
          const savedId = user.subscription?.cycle_id;
          if (savedId != null) {
            const direct = gymCycles.find((c) => Number(c.id) === Number(savedId));
            if (!direct) {
              const all = [...gymCycles, ...(Array.isArray(platCycles) ? platCycles : [])];
              const raw = all.find((c) => Number(c.id) === Number(savedId));
              const startKey = String(raw?.startDate || raw?.start_date || '').slice(0, 10);
              const mirror = startKey
                ? gymCycles.find((c) => String(c.startDate || c.start_date || '').slice(0, 10) === startKey)
                : null;
              if (mirror) baseForm.cycle_id = String(mirror.id);
            } else {
              baseForm.cycle_id = String(direct.id);
            }
          }
        }
      } catch {
        /* keep cycles cargados del listado */
      }
    } else if (user.subscription?.cycle_id != null && !isGymScoped) {
      // Cuenta con ciclo gym-id mientras el listado trae plataforma: remapear a opción visible.
      const savedId = Number(user.subscription.cycle_id);
      const inList = cycles.some((c) => Number(c.id) === savedId);
      if (!inList) {
        try {
          const platRes = await api.get('/cycles', { params: { platform_only: 'true' } });
          const platCycles = platRes?.data?.data || platRes?.data || [];
          const raw = [...cycles, ...platCycles].find((c) => Number(c.id) === savedId)
            || platCycles.find((c) => Number(c.id) === savedId);
          const startKey = String(raw?.startDate || raw?.start_date || '').slice(0, 10);
          const mirror = startKey
            ? cycles.find((c) => String(c.startDate || c.start_date || '').slice(0, 10) === startKey)
              || platCycles.find((c) => String(c.startDate || c.start_date || '').slice(0, 10) === startKey
                && c.gym_id == null)
            : null;
          if (mirror) baseForm.cycle_id = String(mirror.id);
        } catch { /* keep saved id */ }
      }
    }

    const gid = userGymId(user);
    const isSedeAdmin = isSedeManagedUserRow(user, gymById, gyms, gymScopeId);
    const openSedeForm = preferSedeForm === true
      || (preferSedeForm !== false && !isSuperAdmin && isSedeAdmin);

    if (openSedeForm && isSedeAdmin && gid) {
      let sedeGym = gymById[gid] || gyms.find((g) => Number(g.id) === Number(gid));
      let parentProgramsList = [];
      const parentId = sedeGym?.parent_gym_id ?? gymScopeId;
      try {
        const gymRes = await api.get(`/gyms/${gid}`);
        const detail = gymRes.data?.gym || gymRes.data;
        if (detail) sedeGym = { ...sedeGym, ...detail };
        if (parentId) {
          const progRes = await api.get('/programs', { params: { gym_id: parentId, gym_only: 'true' } });
          let list = progRes.data?.data || progRes.data || [];
          parentProgramsList = Array.isArray(list) ? list : [];
          // Padre marca blanca sin programas propios: catálogo plataforma (misma fuente que resolveParentProgramIds).
          if (!parentProgramsList.length) {
            const platRes = await api.get('/programs', { params: { platform_only: 'true' } });
            list = platRes.data?.data || platRes.data || [];
            parentProgramsList = Array.isArray(list) ? list.filter((p) => p.active !== false) : [];
          }
        }
      } catch (loadErr) {
        console.warn('sede edit load partial:', loadErr?.response?.data?.error || loadErr.message);
        if (!sedeGym) {
          setError(t('users.sede_load_error', 'No se pudo cargar los datos de la sede'));
          setFormData(baseForm);
          setEditLoading(false);
          return;
        }
      }
      setParentSedePrograms(parentProgramsList);
      setFormData({
        ...buildSedeFormFromGym(baseForm, sedeGym, gid, parentProgramsList),
        roles: ['admin_sede'],
        planId: user.planId || user.subscription?.plan || '',
        extend_vigencia_days: (user.subscription?.has_account || user.subscription?.operator_license) && user.subscription?.active ? '' : '30',
      });
      setEditLoading(false);
      return;
    }

    if (isGymScoped && userRolesList(user).some((r) => ['usuario_final', 'usuario_final_sede'].includes(r))) {
      baseForm.editKind = 'final_user';
      if (!baseForm.extend_vigencia_days) baseForm.extend_vigencia_days = '30';
      if (!baseForm.fecha_inicio || !baseForm.fecha_vencimiento) {
        Object.assign(baseForm, resolveVigenciaFormDefaults(user, Number(baseForm.extend_vigencia_days) || 30));
      }
    }
    setParentSedePrograms([]);
    setFormData(baseForm);
    setEditLoading(false);
  };

  const startEditSedeByGym = async (sedeGym) => {
    setError('');
    let list = users;
    let adminUser = resolveSedeAdminUser(sedeGym, list);
    // Si el listado de usuarios falló (502) o está vacío, reintentar antes de declarar “sin admin”.
    if (!adminUser) {
      try {
        const res = await api.get('/admin/users');
        list = res.data?.data || [];
        setUsers(list);
        adminUser = resolveSedeAdminUser(sedeGym, list);
      } catch {
        setError(t('users.error_loading', 'Error cargando usuarios'));
        return;
      }
    }
    if (!adminUser) {
      setError(t('users.sede_no_admin', 'No hay administrador asignado a esta sede. Asigna un usuario con el email de contacto de la sede.'));
      return;
    }
    await startEdit(adminUser, { preferSedeForm: true });
  };

  const switchEditToUserMode = () => {
    if (!editingUser) return;
    startEdit(editingUser, { preferSedeForm: false });
  };

  const closeEditModal = () => {
    setEditingUser(null);
    setEditLoading(false);
    setSubscriptionActivating(false);
    setFormData(emptyForm());
    setError('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('users.delete_confirm', '¿Estás seguro de eliminar este usuario?'))) return;
    try {
      await api.delete(`/admin/users/${id}`);
      setUsers((prev) => (prev || []).filter((u) => Number(u.id) !== Number(id)));
      if (editingUser && Number(editingUser.id) === Number(id)) closeEditModal();
      fetchUsers();
      setError('');
    } catch (err) {
      setError(err?.response?.data?.error || t('users.delete_error', 'Error al eliminar usuario'));
    }
  };

  const handleDeleteGym = async (gym) => {
    if (!gym?.id) return;
    const label = gym.nombre || `#${gym.id}`;
    if (!window.confirm(t('gyms.delete_confirm', '¿Eliminar el gimnasio «{name}»? Esta acción no se puede deshacer.').replace('{name}', label))) {
      return;
    }
    try {
      await api.delete(`/gyms/${gym.id}`);
      fetchResources();
      setError('');
    } catch (err) {
      setError(err?.response?.data?.error || t('gyms.delete_error', 'No se pudo eliminar el gimnasio'));
    }
  };

  const handleActivateUser = async (user, { planId, days, cycle_id, finance_zero } = {}) => {
    const activationForm = activationFormContext(user, formData, editingUser);
    const plan = planId || user.subscription?.plan || user.planId || activationForm.planId;
    const vigenciaDays = Number(days ?? activationForm.extend_vigencia_days) || 30;
    const isTraining = isTrainingCoachClient(user, activationForm);
    const isSedeOp = isSedeOperatorUser(user, gymById, gyms)
      || activationForm.editKind === 'sede';
    const isSedeConsumer = isSedeConsumerUserRow(user, gymById, gyms);
    const pendingRegistration = Boolean(user.subscription?.pending_registration
      || (user.subscription?.pending && !user.subscription?.has_account));
    const operatorPending = operatorNeedsActivation(user, gymById, gyms);
    const canAutoResolvePlan = pendingRegistration || isSedeConsumer || Boolean(user.module_access?.d28d_program);
    const estado = String(user.subscription?.estado || '').toLowerCase();
    const isPendingEstado = [
      'pendiente_activacion', 'pendiente_sede', 'pendiente_pago_online',
      'pendiente_habilitacion', 'pendiente',
    ].includes(estado);
    if (!user.subscription?.has_account && !plan && !isTraining && !isSedeOp && !isSedeConsumer
      && !canAutoResolvePlan && !operatorPending) {
      emitToast({
        type: 'error',
        title: t('users.sub_no_account', 'Sin cuenta'),
        message: t('users.sub_select_plan_first', 'Selecciona un plan antes de activar.'),
      });
      return;
    }
    // Solo bloquear operadores de sede ya vigentes — no usuarios finales con licencias previas.
    if (isSedeOp && (
      (user.subscription?.active && user.subscription?.operator_license)
      || hasActiveOperatorLicenses(user?.licenses)
    )) {
      emitToast({
        type: 'info',
        title: t('users.sub_already_active', 'Acceso activo'),
        message: t('users.sub_operator_active', 'El administrador de sede ya tiene vigencia activa.'),
      });
      return;
    }
    if (
      !isPendingEstado
      && !pendingRegistration
      && !user.subscription?.pending
      && user.subscription?.active
      && user.subscription?.has_account
    ) {
      emitToast({
        type: 'info',
        title: t('users.sub_already_active', 'Acceso activo'),
        message: t('users.sub_active_msg', 'El usuario ya tiene acceso activo.'),
      });
      return;
    }
    setSubscriptionActivating(true);
    setError('');
    try {
      const res = await activateUserSubscription(user, {
        days: vigenciaDays,
        plan,
        cycle_id,
        formData: activationForm,
        editingUser,
        gymById,
        gyms,
        finance_zero: Boolean(finance_zero),
      });
      const payload = res?.data?.data || res?.data || {};
      const account = payload.account || null;
      const deferred = Boolean(payload.deferred);
      const untilIso = payload.valid_until
        || account?.fecha_vencimiento
        || new Date(Date.now() + vigenciaDays * 86400000).toISOString();
      const isOperatorActivation = Boolean(payload.operator_activation);
      const activatedSubscription = {
        has_account: !isOperatorActivation,
        account_id: account?.id ?? user.subscription?.account_id ?? null,
        plan: plan || account?.plan || user.subscription?.plan || user.planId || null,
        operator_license: isOperatorActivation,
        active: true,
        pending: false,
        pending_registration: false,
        estado: deferred ? 'pendiente_habilitacion' : 'activo',
        fecha_vencimiento: untilIso,
        days_left: vigenciaDays,
        metodo_pago: account?.metodo_pago || account?.metodoPago || 'confirmado_admin',
      };
      emitToast({
        type: 'success',
        title: deferred
          ? t('users.cycle_deferred_title', 'Inscripción en ciclo futuro')
          : t('users.sub_activated', 'Usuario activado'),
        message: deferred
          ? (payload.message || t('users.cycle_deferred_note', 'Sus servicios se habilitarán un día antes de la fecha de inicio del ciclo inscrito.'))
          : t('users.sub_activated_msg', 'Acceso activado por 30 días').replace('30', String(vigenciaDays)),
      });
      setActivateTarget(null);
      const resUsers = await api.get('/admin/users');
      const list = resUsers.data.data || [];
      setUsers(list);
      if (editingUser) {
        const refreshed = list.find((u) => u.id === user.id);
        const refreshedSub = refreshed?.subscription;
        const useApiSub = refreshedSub?.active || refreshedSub?.operator_license;
        const nextUser = refreshed
          ? { ...refreshed, subscription: useApiSub ? refreshedSub : { ...activatedSubscription, ...(deferred ? { active: false, pending: true } : {}) } }
          : { ...editingUser, subscription: activatedSubscription };
        setEditingUser(nextUser);
        setFormData((prev) => ({
          ...prev,
          planId: nextUser.planId || nextUser.subscription?.plan || prev.planId,
          telefono: nextUser.telefono || prev.telefono || '',
          extend_vigencia_days: '',
        }));
      }
    } catch (e) {
      const message = e.response?.data?.error || t('users.sub_activate_error', 'No se pudo activar');
      setError(message);
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message,
      });
    } finally {
      setSubscriptionActivating(false);
    }
  };

  const handleDeactivateUser = async (user) => {
    if (!window.confirm(t('users.deactivate_confirm', '¿Desactivar el acceso de este usuario?'))) return;
    try {
      await api.post(`/payment-admin/users/${user.id}/deactivate`);
      emitToast({
        type: 'success',
        title: t('users.sub_deactivated', 'Usuario desactivado'),
        message: t('users.sub_deactivated_msg', 'El acceso quedó suspendido'),
      });
      const res = await api.get('/admin/users');
      const list = res.data.data || [];
      setUsers(list);
      if (editingUser) {
        const refreshed = list.find((u) => u.id === user.id);
        if (refreshed) setEditingUser(refreshed);
      }
    } catch (e) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: e.response?.data?.error || t('users.sub_deactivate_error', 'No se pudo desactivar'),
      });
    }
  };

  const handleExtendUser = (user) => {
    setExtendTarget(user);
  };

  const openActivateModal = async (user) => {
    if (isCoachActor || isTrainingCoachClient(user) || isTrainingUsersView) return;
    setError('');
    let fresh = users.find((u) => Number(u.id) === Number(user?.id)) || user;
    let days = remainingVigenciaDays(fresh);
    // Tras desactivar, si la lista no trae days_left/fecha, refrescar para no caer en 30 por defecto.
    if (
      days === 30
      && String(fresh?.subscription?.estado || '').toLowerCase() === 'vencido'
      && !fresh?.subscription?.days_left
      && !fresh?.subscription?.fecha_vencimiento
    ) {
      try {
        const res = await api.get('/admin/users');
        const list = res.data.data || [];
        setUsers(list);
        fresh = list.find((u) => Number(u.id) === Number(user?.id)) || fresh;
        days = remainingVigenciaDays(fresh);
      } catch { /* keep computed days */ }
    }
    setActivateTarget({ ...fresh, activate_days: days });
  };

  const confirmActivateUser = async ({ days, cycle_id, plan, finance_zero }) => {
    if (!activateTarget) return;
    setActivateSaving(true);
    try {
      await handleActivateUser(activateTarget, {
        planId: plan || activateTarget.subscription?.plan || activateTarget.planId,
        days,
        cycle_id,
        finance_zero,
      });
    } finally {
      setActivateSaving(false);
    }
  };

  useEffect(() => {
    if (pendingActivateUserId == null || !users.length) return;
    const target = users.find((u) => Number(u.id) === Number(pendingActivateUserId));
    if (!target) return;
    if (isCoachActor || isTrainingCoachClient(target) || isTrainingUsersView) {
      onPendingActivateConsumed?.();
      return;
    }
    openActivateModal(target);
    onPendingActivateConsumed?.();
  }, [pendingActivateUserId, users]); // eslint-disable-line react-hooks/exhaustive-deps — open once per nav

  const confirmMarkPending = async (days) => {
    if (!pendingTarget) return;
    setPendingSaving(true);
    try {
      await api.post(`/payment-admin/users/${pendingTarget.id}/mark-pending`, {
        days,
        module_code: 'd28d',
        plan: pendingTarget.subscription?.plan || pendingTarget.planId || undefined,
      });
      emitToast({
        type: 'success',
        title: t('users.mark_pending_done', 'Pago marcado como pendiente'),
        message: t('users.mark_pending_done_msg', 'Se programó recordatorio para la fecha límite.'),
      });
      setPendingTarget(null);
      fetchUsers();
    } catch (e) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: e.response?.data?.error || t('users.mark_pending_error', 'No se pudo marcar pago pendiente'),
      });
    } finally {
      setPendingSaving(false);
    }
  };

  const confirmExtendUser = async (days) => {
    if (!extendTarget) return;
    setExtendSaving(true);
    try {
      await api.post(`/payment-admin/extend/${extendTarget.id}`, { module_code: 'd28d', days });
      emitToast({
        type: 'success',
        title: t('vigencias.extended', 'Vigencia extendida'),
        message: t('users.sub_extended_msg', '+{n} días al plan').replace('{n}', String(days)),
      });
      setExtendTarget(null);
      fetchUsers();
    } catch (e) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: e.response?.data?.error || t('users.sub_extend_error', 'No se pudo extender'),
      });
    } finally {
      setExtendSaving(false);
    }
  };

  const getRoleBadgeClass = (rol) => {
    switch (rol) {
      case 'super_admin': return 'badge-purple';
      case 'admin_marca':
      case 'admin_gym':
      case 'admin_gimnasio':
      case 'admin_sede': return 'badge-blue';
      case 'admin_d28d': return 'badge-orange';
      case 'admin_food':
      case 'admin_food_plan': return 'badge-lime';
      case 'admin_training':
      case 'admin_entrenador': return 'badge-amber';
      case 'entrenador': return 'badge-green';
      case 'entrenador_d28d': return 'badge-orange';
      case 'entrenador_gym': return 'badge-orange';
      case 'nutricionista': return 'badge-teal';
      default: return 'badge-slate';
    }
  };

  const formatRole = formatRoleLabel;

  if (showAssignmentsWorkspace && usersWorkspace === 'assignments') {
    return (
      <div className="space-y-6">
        <div className="admin-scope-tabs w-fit">
          <button
            type="button"
            className="px-3 py-1.5 rounded-2xl text-sm font-medium"
            onClick={() => setUsersWorkspace('directory')}
          >
            {t('users.tabs.directory', 'Directorio')}
          </button>
          <button
            type="button"
            className="px-3 py-1.5 rounded-2xl text-sm font-medium is-active"
          >
            {t('assignments.tab', 'Asignaciones')}
          </button>
        </div>
        <UserAssignmentsPanel embedded />
      </div>
    );
  }

  return (
    <div className={`dashboard-main-view surface-readable d28d-admin-surface space-y-6${isTrainingUsersView ? ' training-coach-view training-light-island' : ''}`}>
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-stone-900 font-['Playfair_Display']">
            {scopeMeta?.title || t('users.title', 'Usuarios')}
          </h2>
          {scopeMeta?.subtitle ? (
            <p className="text-stone-600">{scopeMeta.subtitle}</p>
          ) : !scopeMeta?.title ? (
            <p className="text-stone-600">{t('users.subtitle', 'Gestiona usuarios y configuraciones del sistema.')}</p>
          ) : null}
          {showAssignmentsWorkspace ? (
            <div className="mt-3 admin-scope-tabs">
              <button
                type="button"
                className="px-3 py-1.5 rounded-2xl text-sm font-medium is-active"
              >
                {t('users.tabs.directory', 'Directorio')}
              </button>
              <button
                type="button"
                className="px-3 py-1.5 rounded-2xl text-sm font-medium"
                onClick={() => setUsersWorkspace('assignments')}
              >
                {t('assignments.tab', 'Asignaciones')}
              </button>
            </div>
          ) : null}
          {showDomainTabs && usersWorkspace === 'directory' && (
            <div className="mt-3 admin-scope-tabs notranslate" translate="no">
              {getSuperAdminDomainTabs().map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`px-3 py-1.5 rounded-2xl text-sm font-medium ${domainScope === tab.id ? 'is-active' : ''}`}
                  onClick={() => setDomainScope(tab.id)}
                >
                  <span translate="no">{tab.label}</span>
                </button>
              ))}
            </div>
          )}
          {(showPlatformWorkspaceTabs || (!dedicatedScope && !showDomainTabs && !isGymScoped)) && (() => {
            const role = currentUser?.rol;
            const allRoles = Array.isArray(currentUser?.roles) && currentUser.roles.length
              ? currentUser.roles
              : [role].filter(Boolean);
            const isD28d = allRoles.includes('admin_d28d') || isPlatformD28dActor;
            const canManageTrainingOrgs = allRoles.some((r) => [
              'super_admin', 'admin_training', 'admin_entrenador',
            ].includes(r));
            if (!isD28d && !canManageTrainingOrgs) {
              return null;
            }
            return (
              <div className="mt-3 admin-scope-tabs">
                <button type="button" className={`px-3 py-1.5 rounded-2xl ${viewTab === 'users' ? 'is-active' : ''}`} onClick={() => setViewTab('users')}>{t('users.tabs.users', 'Usuarios')}</button>
                {isD28d && (
                  <button type="button" className={`px-3 py-1.5 rounded-2xl ${viewTab === 'gyms' ? 'is-active' : ''}`} onClick={() => { setError(''); setViewTab('gyms'); }}>{t('users.tabs.gyms', 'Gimnasios')}</button>
                )}
                {isD28d && isGymScoped && canCreateChildSedes && (
                  <button type="button" className={`px-3 py-1.5 rounded-2xl ${viewTab === 'sedes' ? 'is-active' : ''}`} onClick={() => setViewTab('sedes')}>{t('users.tabs.sedes', 'Sedes')}</button>
                )}
                {canManageTrainingOrgs && (
                  <button type="button" className={`px-3 py-1.5 rounded-2xl ${viewTab === 'trainers' ? 'is-active' : ''}`} onClick={() => setViewTab('trainers')}>{t('users.tabs.trainers', 'Organizaciones coach')}</button>
                )}
              </div>
            );
          })()}
          {isGymScoped && canCreateChildSedes && (
            <div className="mt-3 admin-scope-tabs">
              <button type="button" className={`px-3 py-1.5 rounded-2xl text-sm ${viewTab === 'users' ? 'is-active' : ''}`} onClick={() => setViewTab('users')}>{t('users.tabs.users', 'Usuarios')}</button>
              <button type="button" className={`px-3 py-1.5 rounded-2xl text-sm ${viewTab === 'sedes' ? 'is-active' : ''}`} onClick={() => setViewTab('sedes')}>{t('users.tabs.sedes', 'Sedes')}</button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
        {isPlatformD28dActor && viewTab === 'gyms' && !fitLiveTrainersTab && (
          <>
            <button
              type="button"
              className="btn-secondary inline-flex items-center gap-2"
              onClick={openPlatformCreateSede}
            >
              <UserPlus className="w-4 h-4" />
              {t('users.create_sede', 'Crear sede')}
            </button>
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-2"
              onClick={() => setShowCreateVirtualGymModal(true)}
            >
              <UserPlus className="w-4 h-4" />
              {t('tenants.create_title', 'Nuevo gimnasio virtual')}
            </button>
          </>
        )}
        {isPlatformD28dActor && viewTab !== 'gyms' && !fitLiveTrainersTab && (
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-2"
            onClick={() => { setError(''); setViewTab('gyms'); }}
          >
            <UserPlus className="w-4 h-4" />
            {t('users.manage_gyms_sedes', 'Gimnasios y sedes')}
          </button>
        )}
        {showScopedUserWorkspace && isGymScoped && canCreateChildSedes && viewTab !== 'gyms' && (
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-2"
            onClick={() => setShowCreateSedeModal(true)}
          >
            <UserPlus className="w-4 h-4" />
            {t('users.new_sede', 'Nueva sede')}
          </button>
        )}
        {showScopedUserWorkspace && viewTab === 'users' && !fitLiveTrainersTab && (
          <>
            <button
              type="button"
              className="btn-secondary inline-flex items-center gap-2"
              disabled={excelBusy}
              onClick={() => setExportStatusPrompt(true)}
            >
              <Download className="w-4 h-4" />
              {t('users.export_excel', 'Descargar Excel')}
            </button>
            <button
              type="button"
              className="btn-secondary inline-flex items-center gap-2"
              disabled={excelBusy}
              onClick={downloadUsersImportTemplate}
            >
              <FileSpreadsheet className="w-4 h-4" />
              {t('users.import_template', 'Plantilla carga')}
            </button>
            <button
              type="button"
              className="btn-secondary inline-flex items-center gap-2"
              disabled={excelBusy}
              onClick={() => usersImportInputRef.current?.click()}
            >
              <Upload className="w-4 h-4" />
              {excelBusy ? t('users.importing', 'Importando…') : t('users.import_excel', 'Cargar usuarios')}
            </button>
            <input
              ref={usersImportInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) runUsersImportExcel(f);
              }}
            />
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-2"
              onClick={() => {
                setEditingUser(null);
                const defaultGymId = isGymScoped
                  ? String(gymScopeId)
                  : (isD28dMemberAdminUi && (userGymFilterId || d28dPlatformGym?.id)
                    ? String(userGymFilterId || d28dPlatformGym.id)
                    : '');
                setFormData({
                  ...emptyForm(),
                  gym_id: defaultGymId,
                  trainer_id: '',
                  gymCreateKind: 'usuario_final',
                  roles: isD28dMemberAdminUi ? ['usuario_final'] : emptyForm().roles,
                  module_access: isD28dMemberAdminUi
                    ? { ...EMPTY_MODULE_ACCESS, d28d: true, live_classes: true }
                    : emptyForm().module_access,
                  // Admin sede: pedir días y activar al crear.
                  extend_vigencia_days: (isSedeActor || isSedeScopeGym) ? '30' : '',
                });
                setShowForm(true);
              }}
            >
              <UserPlus className="w-4 h-4" />
              {isGymScoped ? t('users.new_member', 'Nuevo usuario') : t('users.new_user', 'Nuevo Usuario')}
            </button>
          </>
        )}
        {/* removed duplicate Nuevo usuario block */}
        </div>
      </div>

      {fitLiveTrainersTab ? <FitLiveTrainersRoster /> : null}

      {exportStatusPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="form-modal d28d-admin-modal ui-light-panel surface-readable max-w-md w-full rounded-2xl p-5 shadow-xl">
            <h3 className="font-bold text-stone-900 mb-2">
              {t('users.export_status_title', '¿Qué usuarios descargar?')}
            </h3>
            <p className="text-sm text-stone-600 mb-4">
              {t('users.export_status_hint', 'Elige activos, inactivos (vencidos/pendientes) o ambos.')}
            </p>
            <div className="flex flex-col gap-2">
              <button type="button" className="btn-primary" disabled={excelBusy} onClick={() => downloadUsersExcel('active')}>
                {t('users.export_active', 'Solo activos')}
              </button>
              <button type="button" className="btn-secondary" disabled={excelBusy} onClick={() => downloadUsersExcel('inactive')}>
                {t('users.export_inactive', 'Solo inactivos')}
              </button>
              <button type="button" className="btn-secondary" disabled={excelBusy} onClick={() => downloadUsersExcel('both')}>
                {t('users.export_both', 'Activos e inactivos')}
              </button>
              <button type="button" className="btn-secondary mt-1" onClick={() => setExportStatusPrompt(false)}>
                {t('common.cancel', 'Cancelar')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateSedeParentPicker && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
          <div className="form-modal d28d-admin-modal ui-light-panel surface-readable max-w-md w-full rounded-2xl p-5 shadow-xl">
            <h3 className="font-bold text-stone-900 mb-2">
              {t('users.create_sede_pick_parent', 'Crear sede física')}
            </h3>
            {d28dSedeParentCandidates.length === 0 ? (
              <>
                <p className="text-sm text-stone-600 mb-4">
                  {t(
                    'users.create_sede_no_parent',
                    'No hay un gimnasio padre disponible. Crea primero un gimnasio virtual o verifica el ancla D28D.',
                  )}
                </p>
                <div className="flex flex-wrap gap-2 justify-end">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowCreateSedeParentPicker(false)}
                  >
                    {t('common.cancel', 'Cancelar')}
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => {
                      setShowCreateSedeParentPicker(false);
                      setShowCreateVirtualGymModal(true);
                    }}
                  >
                    {t('tenants.create_title', 'Nuevo gimnasio virtual')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-stone-600 mb-4">
                  {t('users.create_sede_pick_parent_hint', 'Hay varios gimnasios virtuales. Elige el padre de la nueva sede.')}
                </p>
                <label className="block text-sm font-medium text-stone-700 mb-1" htmlFor="create-sede-parent">
                  {t('users.parent_virtual_gym', 'Gimnasio padre')}
                </label>
                <select
                  id="create-sede-parent"
                  className="input w-full mb-4"
                  value={createSedePickerParentId}
                  onChange={(e) => setCreateSedePickerParentId(e.target.value)}
                >
                  {d28dSedeParentCandidates.map((g) => (
                    <option key={g.id} value={String(g.id)}>
                      {gymDisplayLabel(g)}
                      {isD28dPlatformGym(g) ? ' (D28D)' : ''}
                    </option>
                  ))}
                </select>
                <div className="flex flex-wrap gap-2 justify-end">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowCreateSedeParentPicker(false)}
                  >
                    {t('common.cancel', 'Cancelar')}
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => {
                      const parent = d28dSedeParentCandidates.find(
                        (g) => String(g.id) === String(createSedePickerParentId),
                      );
                      if (!parent) return;
                      setShowCreateSedeParentPicker(false);
                      setCreateSedeParent(parent);
                    }}
                  >
                    {t('users.create_sede_continue', 'Continuar')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {isPlatformD28dActor && isGymScoped && (
        <div className="flex flex-wrap items-center gap-2 text-sm rounded-2xl border border-lime-300 bg-lime-50 px-4 py-3">
          <span className="text-stone-700">
            {t('users.operating_gym', 'Operando:')}
            {' '}
            <strong className="text-stone-900">{resolvedGymName}</strong>
            {' '}
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${gymKindMeta(scopeGym, t).badgeClass}`}>
              {gymKindMeta(scopeGym, t).label}
            </span>
          </span>
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={() => pickVirtualGym('')}
          >
            {t('users.exit_manage_gym', 'Salir de gestión')}
          </button>
          {canCreateChildSedes && (
            <button
              type="button"
              className="btn-primary text-xs"
              onClick={() => setCreateSedeParent(scopeGym)}
            >
              {t('users.new_sede', 'Nueva sede física')}
            </button>
          )}
        </div>
      )}

      {isGymScoped && gymScopeId && (
        <div className="bg-stone-50 border border-stone-200 rounded-2xl p-4 text-sm">
          <p className="font-semibold text-stone-900">{t('users.gym_invite_code', 'Código de invitación de tu gimnasio')}</p>
          <p className="text-stone-600 mt-1">{t('users.gym_invite_hint', 'Comparte este código para que nuevos usuarios se registren en tu sede.')}</p>
          <div className="mt-2">
            <InviteCodeCell
              value={gymById[gymScopeId]?.invite_code || ''}
              onSave={(code) => saveGymInviteCode(gymScopeId, code)}
              onGenerate={() => generateGymInviteCode(gymScopeId)}
              shareLabel={resolvedGymName || scopeGym?.nombre || 'Gimnasio'}
            />
          </div>
        </div>
      )}

      {error && showScopedUserWorkspace && viewTab === 'users' && (
        <div className="bg-red-950/40 border-l-4 border-red-500 p-4 rounded-md">
          <p className="text-sm text-red-300 font-medium">{error}</p>
        </div>
      )}

      {isPlatformD28dActor && !isGymScoped && viewTab === 'users' && !fitLiveTrainersTab && (
        <div className="bg-sky-50 border border-sky-200 rounded-2xl p-4 text-sm text-stone-700 flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <p className="font-semibold text-stone-900">{t('users.d28d_filter_title', 'Filtrar por gimnasio')}</p>
            <p className="text-stone-600 mt-0.5">{t('users.d28d_filter_hint', 'Por defecto ves todos. Elige un gym o sede para acotar el listado.')}</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <button
              type="button"
              className="btn-primary text-sm whitespace-nowrap"
              onClick={() => { setError(''); setViewTab('gyms'); }}
            >
              {t('users.go_to_gyms', 'Ir a Gimnasios')}
            </button>
            <select
              className="input sm:max-w-xs"
              value={userGymFilterId}
              onChange={(e) => {
                setManageGymId('');
                try { sessionStorage.removeItem('admin.users.d28dGymId'); } catch { /* ignore */ }
                setUserGymFilterId(e.target.value);
              }}
            >
              <option value="">{t('users.filter_all_gyms', 'Todos')}</option>
              {d28dFilterableGyms.map((gym) => (
                <option key={gym.id} value={gym.id}>
                  {gymSelectOptionLabel(gym, t)}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Gyms — admin D28D: gyms virtuales + sedes + códigos */}
      {viewTab === 'gyms' && isPlatformD28dActor && !fitLiveTrainersTab && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden ui-light-panel">
          <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h4 className="text-md font-semibold text-stone-900">{t('users.gyms_d28d_title', 'Gimnasios virtuales y sedes')}</h4>
              <p className="text-xs text-stone-500 mt-0.5">{t('users.gyms_d28d_hint', 'Un solo ecosistema D28D: cada gym virtual puede tener sedes físicas con su propio código.')}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" className="btn-secondary text-sm" onClick={openPlatformCreateSede}>
                {t('users.create_sede', 'Crear sede')}
              </button>
              <button type="button" className="btn-primary text-sm" onClick={() => setShowCreateVirtualGymModal(true)}>
                {t('tenants.create_title', 'Nuevo gimnasio virtual')}
              </button>
            </div>
          </div>
          <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap gap-3 text-xs">
            <span className="inline-flex items-center gap-1.5"><span className="badge-blue px-2 py-0.5 rounded-full">{t('gyms.kind_virtual', 'Gym virtual')}</span> Marca blanca principal</span>
            <span className="inline-flex items-center gap-1.5"><span className="badge-teal px-2 py-0.5 rounded-full">{t('gyms.kind_sede', 'Sede física')}</span> Sucursal con código propio</span>
          </div>
          <div className="p-4 border-b border-slate-200">
            <div className="relative w-full sm:max-w-md">
              <Search className="w-4 h-4 text-stone-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="search"
                className="input pl-9 w-full"
                placeholder={t('gyms.search_ph', 'Buscar por nombre, ciudad o dirección...')}
                value={searchGym}
                onChange={(e) => setSearchGym(e.target.value)}
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">{t('common.name', 'Nombre')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">{t('gyms.type', 'Tipo')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">{t('common.city', 'Ciudad')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">{t('users.invite_code', 'Código invitación')}</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-stone-600 uppercase">{t('common.actions', 'Acciones')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {d28dGymsTableRows.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-sm text-stone-600">
                      {searchGym.trim() ? t('users.none_search', 'No hay resultados.') : t('gyms.none', 'No hay gimnasios registrados.')}
                    </td>
                  </tr>
                ) : d28dGymsTableRows.map(({ gym: g, kind, depth }) => {
                  const meta = gymKindMeta(g, t);
                  return (
                    <tr key={g.id} className={`hover:bg-stone-50 ${meta.rowClass}`}>
                      <td className="px-6 py-4 text-sm font-medium text-stone-900">
                        <span style={{ paddingLeft: depth ? '1.25rem' : 0 }}>
                          {depth ? '↳ ' : ''}{gymDisplayLabel(g)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${meta.badgeClass}`}>{meta.label}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-600">{g.ciudad || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <InviteCodeCell
                          value={g.invite_code}
                          onSave={(code) => saveGymInviteCode(g.id, code)}
                          onGenerate={() => generateGymInviteCode(g.id)}
                          shareLabel={gymDisplayLabel(g)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="inline-flex flex-wrap gap-2 justify-end">
                          {kind === 'virtual' ? (
                            <>
                              <button type="button" className="btn-secondary text-xs" onClick={() => setCreateSedeParent(g)}>
                                {t('users.new_sede', 'Nueva sede')}
                              </button>
                              <button type="button" className="btn-primary text-xs" onClick={() => pickVirtualGym(String(g.id))}>
                                {t('users.manage_gym', 'Gestionar')}
                              </button>
                            </>
                          ) : (
                            <button type="button" className="btn-secondary text-xs" onClick={() => startEditSedeByGym(g)}>
                              {t('users.edit_sede', 'Editar sede')}
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn-secondary text-xs"
                            onClick={() => {
                              setManageGymId('');
                              try { sessionStorage.removeItem('admin.users.d28dGymId'); } catch { /* ignore */ }
                              setUserGymFilterId(String(g.id));
                              setViewTab('users');
                            }}
                          >
                            {t('users.view_users', 'Ver usuarios')}
                          </button>
                          {hasAnyRole(['super_admin', 'admin_d28d']) && !isPlatformSeedGym(g) ? (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-2xl border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 transition-colors text-xs"
                              onClick={() => handleDeleteGym(g)}
                            >
                              {t('common.delete', 'Eliminar')}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Gyms Summary — super_admin u otros roles */}
      {viewTab === 'gyms' && !isPlatformD28dActor && !fitLiveTrainersTab && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden ui-light-panel">
          <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h4 className="text-md font-semibold text-stone-900">{t('users.gyms_summary', 'Resumen de Gimnasios')}</h4>
            <div className="relative w-full sm:max-w-xs">
              <Search className="w-4 h-4 text-stone-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="search"
                className="input pl-9 w-full"
                placeholder={t('gyms.search_ph', 'Buscar por nombre, ciudad o dirección...')}
                value={searchGym}
                onChange={(e) => setSearchGym(e.target.value)}
                aria-label={t('common.search', 'Buscar')}
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('common.id', 'ID')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('common.name', 'Nombre')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('common.city', 'Ciudad')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('users.invite_code', 'Código invitación')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('gyms.type', 'Tipo')}</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('plans.capacity', 'Cupo Máx')}</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('common.actions', 'Acciones')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredGyms.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-sm text-stone-600">
                      {searchGym.trim()
                        ? t('users.none_search', 'No hay resultados para tu búsqueda.')
                        : t('gyms.none', 'No hay gimnasios registrados.')}
                    </td>
                  </tr>
                ) : filteredGyms.map(g => {
                  const meta = gymKindMeta(g, t);
                  return (
                  <tr key={g.id} className={`hover:bg-stone-100 transition-colors ${meta.rowClass}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-600">#{g.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-stone-900">{gymDisplayLabel(g)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-600">{g.ciudad || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <InviteCodeCell
                        value={g.invite_code}
                        onSave={(code) => saveGymInviteCode(g.id, code)}
                        onGenerate={() => generateGymInviteCode(g.id)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${meta.badgeClass}`}>{meta.label}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-stone-600">{g.capacidad_usuarios ?? 50}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="inline-flex flex-wrap gap-2 justify-end">
                      <button
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-2xl border border-stone-300 text-stone-700 bg-white hover:bg-stone-100 transition-colors"
                        onClick={() => {
                          const list = users.filter((u) => (
                            (u.gym_id || u.gymId) === g.id
                            && isD28dConsumerFinalUser(u)
                          ));
                          setAssignedTitle(t('companies.users_of_gym', 'Usuarios del Gimnasio: {name}').replace('{name}', g.nombre));
                          setAssignedList(list);
                          setShowAssignedModal(true);
                        }}
                      >
                        {t('users.view_users', 'Ver usuarios')}
                      </button>
                      {isPlatformD28dActor && !g.parent_gym_id ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-2xl border border-lime-300 text-stone-800 bg-lime-50 hover:bg-lime-100 transition-colors"
                          onClick={() => pickVirtualGym(String(g.id))}
                        >
                          {t('users.manage_gym', 'Gestionar')}
                        </button>
                      ) : null}
                      {hasAnyRole(['super_admin', 'admin_d28d']) && !isPlatformSeedGym(g) ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-2xl border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
                          onClick={() => handleDeleteGym(g)}
                        >
                          {t('common.delete', 'Eliminar')}
                        </button>
                      ) : null}
                      </div>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showScopedUserWorkspace && (
      <>
      {/* Form Section (solo creación) */}
      {showForm && !editingUser && (
      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 ui-light-panel">
        <div className="flex items-center justify-between mb-4 border-b border-slate-200 pb-2">
          <h4 className="text-md font-semibold text-stone-900">
            {isGymSimpleForm && formData.gymCreateKind === 'sede'
              ? t('users.create_sede', 'Crear nueva sede')
              : t('users.create_user', 'Crear Nuevo Usuario')}
          </h4>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              setShowForm(false);
              setFormData(emptyForm());
            }}
          >
            {t('common.close', 'Cerrar')}
          </button>
        </div>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {isCoachActor && (
            <div className="md:col-span-2 rounded-xl border border-lime-200 bg-lime-50/80 p-4 text-sm text-stone-700 space-y-3">
              <div>
                <p className="font-semibold text-stone-900">{t('users.coach_client_hint_title', 'Cliente de entrenamiento')}</p>
                <p>
                  {t(
                    'users.coach_client_hint_body',
                    'Solo creas usuarios finales vinculados a tu marca. No se asigna gimnasio D28D. Elige el plan de activación (mensual/bimestral/trimestral); la vigencia se aplica al pagar en Activar clientes. El Plan de alimentación no se asigna aquí: el cliente debe comprarlo en Mi cuenta (pasarela Food Plan).',
                  )}
                </p>
              </div>
              <div>
                <label className="label">{t('common.trainer', 'Entrenador')}</label>
                <p className="input bg-stone-50 text-stone-800 font-medium">
                  {coachTrainerLabel}
                  {coachTrainerId != null ? (
                    <span className="text-stone-500 font-normal text-xs ml-2">#{coachTrainerId}</span>
                  ) : null}
                </p>
              </div>
              <div>
                <label className="label">{t('plans.subscription', 'Plan de activación')} *</label>
                {coachTrainingPlans.length === 0 ? (
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    {t(
                      'plans.coach_activation_empty',
                      'No hay planes Training (audiencia coach). Créalos en Maestros → Plan Entrenadores (mensual/bimestral/trimestral).',
                    )}
                  </p>
                ) : (
                  <>
                    <select
                      name="planId"
                      value={formData.planId}
                      onChange={handleInputChange}
                      required
                      className="input"
                    >
                      <option value="">{t('common.select', 'Seleccionar…')}</option>
                      {coachTrainingPlans.map((p) => (
                        <option key={p.nombre} value={p.nombre}>
                          {(p.display_nombre || p.nombre).toUpperCase()}
                          {p.cycles_count ? ` · ${p.cycles_count} mes${Number(p.cycles_count) === 1 ? '' : 'es'}` : ''}
                          {p.precio_mensual != null ? ` · $${Number(p.precio_mensual).toLocaleString('es-CO')}` : ''}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-stone-500 mt-1">
                      {t(
                        'plans.coach_activation_hint',
                        'La duración (días/meses) la define el plan al activar con Wompi. Puedes cambiar el plan después en Editar usuario.',
                      )}
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
          {isGymSimpleForm && (
            <div className="md:col-span-2">
              <label className="label">{t('users.create_kind', '¿Qué deseas crear?')}</label>
              <div className="flex flex-wrap gap-4 mt-1">
                <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                  <input
                    type="radio"
                    name="gymCreateKind"
                    checked={formData.gymCreateKind === 'usuario_final'}
                    onChange={() => setFormData((prev) => ({ ...prev, gymCreateKind: 'usuario_final', roles: ['usuario_final'] }))}
                    className="text-lime-600 focus:ring-lime-500"
                  />
                  {t('users.kind_final', 'Usuario final')}
                </label>
                {canCreateChildSedes && (
                  <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                    <input
                      type="radio"
                      name="gymCreateKind"
                      checked={formData.gymCreateKind === 'sede'}
                      onChange={() => setFormData((prev) => ({ ...prev, gymCreateKind: 'sede' }))}
                      className="text-lime-600 focus:ring-lime-500"
                    />
                    {t('users.kind_sede', 'Sede (marca blanca)')}
                  </label>
                )}
              </div>
              <p className="text-xs text-stone-500 mt-2">
                {formData.gymCreateKind === 'sede'
                  ? t('users.kind_sede_hint', 'La sede tendrá su propio administrador (usuarios y asistencias). Puedes personalizar marca blanca.')
                  : t('users.kind_final_hint', 'Acceso a progreso, clases en vivo y galería de tu gimnasio.')}
              </p>
            </div>
          )}
          <div>
            <label className="label">
              {isGymSimpleForm && formData.gymCreateKind === 'sede'
                ? t('orgs.field_sede_name', 'Nombre de la sede')
                : t('common.name', 'Nombre')}
            </label>
            <input 
              name="nombre" 
              value={formData.nombre} 
              onChange={handleInputChange} 
              required 
              className="input"
            />
          </div>
          <div>
            <label className="label">
              {isGymSimpleForm && formData.gymCreateKind === 'sede'
                ? t('tenants.field_contact_email', 'Email de contacto')
                : t('common.email', 'Email')}
            </label>
            <input 
              name="email" 
              value={formData.email} 
              onChange={handleInputChange} 
              required 
              className="input"
            />
            {users.some((u) => (
              Number(u.id) !== Number(editingUser?.id)
              && String(u.email).toLowerCase() === String(formData.email).toLowerCase()
            )) && (
              <p className="mt-1 text-sm text-red-600">{t('users.email_exists', 'Este email ya está registrado')}</p>
            )}
          </div>
          {!(isGymSimpleForm && formData.gymCreateKind === 'sede') && (
            <div>
              <label className="label">{t('auth.password', 'Contraseña')}</label>
              <input 
                name="password" 
                type="password" 
                value={formData.password} 
                onChange={handleInputChange} 
                required 
                className="input"
              />
            </div>
          )}
          {!(isGymSimpleForm && formData.gymCreateKind === 'sede') && (
            <PhoneField
              id="admin-create-telefono"
              label={t('common.phone', 'Teléfono / WhatsApp')}
              value={formData.telefono || ''}
              onChange={(next) => setFormData((prev) => ({ ...prev, telefono: next }))}
              hint={t('users.phone_hint', 'Incluye el indicativo del país para habilitar WhatsApp (ej. Colombia +57).')}
            />
          )}
          {isGymSimpleForm && formData.gymCreateKind === 'sede' && (
            <>
              <div>
                <label className="label">{t('orgs.sede_plan', 'Plan de la sede')} *</label>
                <select
                  name="sede_plan_id"
                  value={formData.sede_plan_id || ''}
                  onChange={handleInputChange}
                  className="input"
                  required
                >
                  <option value="">{t('orgs.sede_plan_placeholder', 'Selecciona un plan de sedes')}</option>
                  {sedeCatalogPlans.map((p) => (
                    <option key={p.nombre} value={p.nombre}>
                      {(p.display_nombre || p.nombre)}
                      {Number(p.precio_mensual) === 0 && Number(p.precio_mensual_usd) === 0
                        ? ' · $0'
                        : ` · $${Number(p.precio_mensual || 0).toLocaleString('es-CO')} COP`}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-stone-500 mt-1">
                  {t(
                    'orgs.sede_plan_hint',
                    'Obligatorio. Solo este plan se mostrará en registro y alta de usuarios de la sede.',
                  )}
                </p>
                {!sedeCatalogPlans.length ? (
                  <p className="text-xs text-amber-700 mt-1">
                    {t('orgs.sede_plan_empty', 'No hay planes con check Sedes. Créalos en Maestros → Planes D28D antes de abrir la sede.')}
                  </p>
                ) : null}
              </div>
              <div>
                <label className="label">{t('common.city', 'Ciudad')}</label>
                <input
                  name="sede_ciudad"
                  value={formData.sede_ciudad}
                  onChange={handleInputChange}
                  className="input"
                  placeholder={scopeGym?.ciudad || ''}
                />
              </div>
              <div>
                <label className="label">{t('orgs.sede_member_fee', 'Valor de cobro (COP)')}</label>
                <input
                  name="sede_member_fee_cop"
                  type="number"
                  min="0"
                  step="1000"
                  value={formData.sede_member_fee_cop}
                  onChange={handleInputChange}
                  className="input"
                  placeholder="0"
                />
                <p className="text-xs text-stone-500 mt-1">
                  {t('orgs.sede_member_fee_hint', 'Referencia para cobro en sede. Los usuarios registrados por código no pagan en línea.')}
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                  <input
                    type="checkbox"
                    name="sede_allow_food_addon"
                    checked={formData.sede_allow_food_addon !== false}
                    onChange={handleInputChange}
                  />
                  {t('orgs.sede_allow_food_addon', 'Mostrar «Adquirir FOOD» a usuarios de la sede')}
                </label>
              </div>
              <div>
                <label className="label">{t('tenants.field_admin_email', 'Email admin (login)')}</label>
                <input
                  name="sede_admin_email"
                  type="email"
                  value={formData.sede_admin_email}
                  onChange={handleInputChange}
                  className="input"
                  placeholder={formData.email || t('tenants.admin_email_hint', 'Puede ser el mismo email de contacto')}
                />
              </div>
              <div>
                <label className="label">{t('tenants.field_admin_password', 'Contraseña admin')}</label>
                <input
                  name="sede_admin_password"
                  type="password"
                  value={formData.sede_admin_password}
                  onChange={handleInputChange}
                  className="input"
                  autoComplete="new-password"
                />
              </div>
              <div className="md:col-span-2">
                <fieldset className="border border-slate-200 rounded-xl p-4 space-y-3">
                  <legend className="text-sm font-semibold text-stone-800 px-1">
                    {t('orgs.sede_calendar_programs', 'Programas visibles en calendario')}
                  </legend>
                  {parentSedePrograms.length ? (
                    <ul className="space-y-2 max-h-40 overflow-y-auto">
                      {parentSedePrograms.map((prog) => (
                        <li key={prog.id}>
                          <label className="flex items-center gap-2 text-sm text-stone-800 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.sede_calendar_program_ids.some((id) => String(id) === String(prog.id))}
                              onChange={() => {
                                setFormData((prev) => {
                                  const id = prog.id;
                                  const has = prev.sede_calendar_program_ids.some((p) => String(p) === String(id));
                                  return {
                                    ...prev,
                                    sede_calendar_program_ids: has
                                      ? prev.sede_calendar_program_ids.filter((p) => String(p) !== String(id))
                                      : [...prev.sede_calendar_program_ids, id],
                                  };
                                });
                              }}
                            />
                            <span>{prog.name || prog.id}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-amber-700">
                      {t('orgs.sede_no_parent_programs', 'El gimnasio virtual padre aún no tiene programas.')}
                    </p>
                  )}
                </fieldset>
              </div>
              <div className="md:col-span-2">
                <GymMinimalBrandingFields
                  formData={{
                    brand_name: formData.sede_brand_name,
                    logo_url: formData.sede_logo_url,
                    primary_color: formData.sede_primary_color,
                    secondary_color: formData.sede_secondary_color,
                    support_whatsapp: formData.sede_support_whatsapp,
                    welcome_message: formData.sede_welcome_message,
                  }}
                  setFormData={(updater) => {
                    setFormData((prev) => {
                      const current = {
                        brand_name: prev.sede_brand_name,
                        logo_url: prev.sede_logo_url,
                        primary_color: prev.sede_primary_color,
                        secondary_color: prev.sede_secondary_color,
                        support_whatsapp: prev.sede_support_whatsapp,
                        welcome_message: prev.sede_welcome_message,
                      };
                      const next = typeof updater === 'function' ? updater(current) : updater;
                      return {
                        ...prev,
                        sede_brand_name: next.brand_name ?? prev.sede_brand_name,
                        sede_logo_url: next.logo_url ?? prev.sede_logo_url,
                        sede_primary_color: next.primary_color ?? prev.sede_primary_color,
                        sede_secondary_color: next.secondary_color ?? prev.sede_secondary_color,
                        sede_support_whatsapp: next.support_whatsapp ?? prev.sede_support_whatsapp,
                        sede_welcome_message: next.welcome_message ?? prev.sede_welcome_message,
                      };
                    });
                  }}
                  showBrandName
                />
              </div>
            </>
          )}
          {((isGymSimpleForm && formData.gymCreateKind === 'usuario_final')
            || (!isGymSimpleForm && !isCoachActor && !formData.roles.includes('entrenador')
              && (formData.roles.includes('usuario_final') || formData.roles.includes('usuario_final_sede'))
              && !isTrainingClientForm))
            && (
          <div>
            <label className="label">
              {hidePlanForSede
                ? t('plans.sede_subscription', 'Plan sede')
                : t('plans.subscription', 'Plan de Suscripción')}
            </label>
            {hidePlanForSede && sedeAudiencePlans.length === 0 ? (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {t(
                  'plans.sede_subscription_empty',
                  'No hay planes de sede activos. En Maestros → Planes, crea un plan D28D con el check «Sedes» (o crea el plan desde esta sede).',
                )}
              </p>
            ) : (
              <>
                <select
                  name="planId"
                  value={formData.planId}
                  onChange={handleInputChange}
                  required={hidePlanForSede ? sedeAudiencePlans.length > 0 : !hidePlanForSede}
                  className="input"
                >
                  <option value="">{t('common.none', 'Ninguno')}</option>
                  {(hidePlanForSede ? sedeAudiencePlans : plans).map((p) => (
                    <option key={p.nombre} value={p.nombre}>
                      {(p.display_nombre || p.nombre).toUpperCase()}
                    </option>
                  ))}
                </select>
                {hidePlanForSede ? (
                  <p className="text-xs text-stone-500 mt-1">
                    {t(
                      'plans.sede_subscription_hint_active',
                      'Solo planes marcados como Sedes. Indica los días activos: el usuario se activa al crearlo.',
                    )}
                  </p>
                ) : null}
                {((isSedeActor || isSedeScopeGym) && formData.gymCreateKind === 'usuario_final') || (foodPlanSelected && !editingUser) ? (
                  <div className="mt-3">
                    <label className="label" htmlFor="create-extend-days">
                      {t('users.days_active_create', 'Días activo')}
                    </label>
                    <input
                      id="create-extend-days"
                      type="number"
                      min={1}
                      max={365}
                      name="extend_vigencia_days"
                      className="input mt-1"
                      value={formData.extend_vigencia_days || ''}
                      onChange={handleInputChange}
                      required
                      placeholder="30"
                    />
                    <p className="text-xs text-stone-500 mt-1">
                      {t(
                        'users.days_active_create_hint',
                        'Entre 1 y 365. Al guardar, el usuario queda activo de inmediato con esta vigencia.',
                      )}
                    </p>
                  </div>
                ) : null}
                {isD28dMemberAdminUi && formData.planId && createFormCycles.length > 0 ? (
                  <div className="mt-3">
                    <label className="label">
                      {createPlanCyclesIncluded > 1
                        ? t('users.activation_cycles_pack', 'Paquete de ciclos de activación')
                        : t('users.activation_cycle', 'Ciclo de activación')}
                    </label>
                    {createPlanCyclesIncluded > 1 ? (
                      <>
                        <p className="text-xs text-stone-500 mt-1 mb-2">
                          {t(
                            'users.activation_cycles_pack_hint',
                            'Este plan incluye {n} ciclos consecutivos. Elige el ciclo de inicio; se marcan los siguientes automáticamente.',
                          ).replace('{n}', String(createPlanCyclesIncluded))}
                        </p>
                        <div className="max-h-48 overflow-y-auto rounded-xl border border-stone-200 bg-white p-2 space-y-1">
                          {createFormCycles.map((c) => {
                            const id = String(c.id);
                            const checked = createSelectedCycleIds.includes(id);
                            return (
                              <label
                                key={c.id}
                                className={`flex items-start gap-2 rounded-lg px-2 py-1.5 text-sm cursor-pointer ${
                                  checked ? 'bg-amber-50 border border-amber-200' : 'hover:bg-stone-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  className="mt-1"
                                  checked={checked}
                                  onChange={() => pickCreateCyclePackStart(id)}
                                />
                                <span>{adminCycleOptionLabel(c)}</span>
                              </label>
                            );
                          })}
                        </div>
                        <p className="text-xs text-stone-500 mt-1">
                          {t('users.activation_cycles_selected', 'Activo en')}:{' '}
                          <strong>{createSelectedCycleIds.length}/{createPlanCyclesIncluded}</strong>
                        </p>
                      </>
                    ) : (
                      <>
                        <select
                          name="cycle_id"
                          value={formData.cycle_id || ''}
                          onChange={handleInputChange}
                          required
                          className="input"
                        >
                          <option value="">{t('users.cycle_select_ph', 'Selecciona el ciclo')}</option>
                          {createFormCycles.map((c) => (
                            <option key={c.id} value={c.id}>{adminCycleOptionLabel(c)}</option>
                          ))}
                        </select>
                        <p className="text-xs text-stone-500 mt-1">
                          {t(
                            'users.activation_cycle_hint',
                            'Ciclo en el que se activará el usuario. Solo vigentes o posteriores.',
                          )}
                        </p>
                      </>
                    )}
                  </div>
                ) : null}
              </>
            )}
          </div>
          )}
          {!isGymSimpleForm && isSuperAdmin && (
          <div className="md:col-span-2">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <label className="label">{t('common.roles', 'Roles')}</label>
              <p className="text-xs text-stone-500">
                {t('users.multi_role_hint', 'Puedes asignar varios roles a la misma persona (ej: Entrenador + Admin FOOD_PLAN).')}
              </p>
            </div>
            {formData.roles.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1">
                {formData.roles.map((r) => (
                  <span key={r} className="badge badge-blue text-xs px-2 py-0.5 whitespace-nowrap">
                    {formatRole(r)}
                  </span>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
              {roleOptionsForForm.map(role => (
                <label key={role.value} className="flex items-center gap-2 text-sm text-stone-700 bg-stone-50 p-2 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-100">
                  <input
                    type="checkbox"
                    checked={formData.roles.includes(role.value)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData(prev => ({
                        ...prev,
                        roles: checked 
                          ? [...prev.roles, role.value] 
                          : prev.roles.filter(r => r !== role.value)
                      }));
                    }}
                    className="rounded border-stone-300 text-lime-600 focus:ring-lime-500"
                  />
                  {role.label}
                </label>
              ))}
            </div>
          </div>
          )}
          {!isGymSimpleForm && isSuperAdmin && formData.roles.includes('entrenador') && (
            <label className="md:col-span-2 flex items-start gap-2 text-sm text-stone-700 bg-lime-50 p-3 rounded-lg border border-lime-200 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.copy_gallery_from_nico !== false}
                onChange={(e) => setFormData((prev) => ({ ...prev, copy_gallery_from_nico: e.target.checked }))}
                className="mt-0.5 rounded border-stone-300 text-lime-600 focus:ring-lime-500"
              />
              <span>
                Copiar galería de ejercicios de Nicolás del Río (plantilla con URLs de video)
              </span>
            </label>
          )}
          {!isGymSimpleForm && isSuperAdmin && formData.roles.includes('entrenador') && (
            <label className="md:col-span-2 flex items-start gap-2 text-sm text-stone-700 bg-lime-50 p-3 rounded-lg border border-lime-200 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.copy_routines_from_nico !== false}
                onChange={(e) => setFormData((prev) => ({ ...prev, copy_routines_from_nico: e.target.checked }))}
                className="mt-0.5 rounded border-stone-300 text-lime-600 focus:ring-lime-500"
              />
              <span>
                Copiar rutinas plantilla de Nicolás del Río (catálogo privado editable)
              </span>
            </label>
          )}
          {(formData.roles.includes('usuario_final') && !isGymSimpleForm && !isCoachActor) && (
            <>
              {!isCoachActor && !isGymScoped && !foodPlanSelected && (
              <div>
                <label className="label">{t('common.gym', 'Gimnasio')}</label>
                <select 
                  name="gym_id" 
                  value={formData.gym_id} 
                  onChange={handleInputChange}
                  required={!editingUser && !foodPlanSelected}
                  className="input"
                >
                  {!isD28dMemberAdminUi || foodPlanSelected ? (
                    <option value="">{t('common.none', 'Ninguno')}</option>
                  ) : null}
                  {memberAssignableGyms.map((g) => (
                    <option key={g.id} value={g.id}>{gymSelectOptionLabel(g, t)}</option>
                  ))}
                </select>
                {isD28dMemberAdminUi && !foodPlanSelected ? (
                  <p className="text-xs text-stone-500 mt-1">
                    {t(
                      'users.d28d_gym_assign_hint',
                      'Incluye D28D, gyms virtuales y sedes. Por defecto D28D.',
                    )}
                  </p>
                ) : null}
                {foodPlanSelected ? (
                  <p className="text-xs text-stone-500 mt-1">
                    {t(
                      'users.food_no_gym_hint',
                      'Food Plan no usa gimnasio ni clases D28D. Solo se activa Plan de alimentación.',
                    )}
                  </p>
                ) : null}
              </div>
              )}
              {isGymScoped && (
              <div>
                <label className="label">{t('common.gym', 'Gimnasio')}</label>
                <p className="input bg-stone-50 text-stone-700">{resolvedGymName || gymById[gymScopeId]?.nombre || t('gym.masters.your_gym', 'Tu gimnasio virtual')}</p>
              </div>
              )}
              {!isCoachActor && !isD28dMemberAdminUi && (
              <div>
                <label className="label">{t('common.trainer', 'Entrenador')}</label>
                <select 
                  name="trainer_id" 
                  value={formData.trainer_id} 
                  onChange={handleInputChange}
                  className="input"
                >
                  <option value="">{t('common.none', 'Ninguno')}</option>
                  {trainers.map(tr => <option key={tr.id} value={tr.id}>{tr.nombre}</option>)}
                </select>
              </div>
              )}
              {isSuperAdmin && (
              <div className="md:col-span-2">
                <label className="label">{t('users.modules', 'Módulos activos')}</label>
                <p className="text-xs text-stone-500 mb-2">
                  {t('users.modules_hint', 'Activa o desactiva acceso a D28D, entrenamiento, food y clases en vivo.')}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {MODULE_OPTIONS.map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 text-sm text-stone-700 bg-stone-50 p-2 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-100">
                      <input
                        type="checkbox"
                        checked={foodPlanSelected ? key === 'food_plan' : Boolean(formData.module_access?.[key])}
                        disabled={foodPlanSelected && key !== 'food_plan'}
                        onChange={() => {
                          if (foodPlanSelected) return;
                          setFormData((prev) => ({
                            ...prev,
                            module_access: {
                              ...prev.module_access,
                              [key]: !prev.module_access?.[key],
                            },
                          }));
                        }}
                        className="rounded border-stone-300 text-lime-600 focus:ring-lime-500"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              )}
            </>
          )}
          <div className="md:col-span-2 flex justify-end">
            <button 
              type="submit" 
              className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={
                !formData.nombre ||
                !formData.email ||
                (!editingUser && (isGymSimpleForm && formData.gymCreateKind === 'sede'
                  ? false
                  : !formData.password)) ||
                (!editingUser && isGymSimpleForm && formData.gymCreateKind === 'usuario_final' && !hidePlanForSede && !formData.planId) ||
                (!editingUser && !isGymSimpleForm && !isCoachActor && formData.roles.includes('usuario_final') && !formData.roles.includes('entrenador') && !isTrainingClientForm && !hidePlanForSede && !formData.planId) ||
                (!editingUser && formData.roles.includes('usuario_final') && !isCoachActor && !isGymScoped && !isTrainingClientForm && !foodPlanSelected && !formData.gym_id) ||
                (!editingUser && formData.gymCreateKind !== 'sede' && users.some(u => String(u.email).toLowerCase() === String(formData.email).toLowerCase())) ||
                (!isGymSimpleForm && isSuperAdmin && formData.roles.length === 0)
              }
            >
              {t('common.save', 'Guardar')}
            </button>
          </div>
        </form>
      </div>
      )}

      {/* Trainers Summary */}
      {viewTab === 'trainers' && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden ui-light-panel">
          <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h4 className="text-md font-semibold text-stone-900">{t('users.trainers_summary', 'Resumen de Entrenadores')}</h4>
            <div className="relative w-full sm:max-w-xs">
              <Search className="w-4 h-4 text-stone-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="search"
                className="input pl-9 w-full"
                placeholder={t('trainers.search_ph', 'Buscar entrenador...')}
                value={searchTrainer}
                onChange={(e) => setSearchTrainer(e.target.value)}
                aria-label={t('common.search', 'Buscar')}
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('common.id', 'ID')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('common.name', 'Nombre')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('trainers.specialty', 'Especialidad')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('users.invite_code', 'Código invitación')}</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('plans.capacity', 'Cupo Máx')}</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('common.actions', 'Acciones')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredTrainers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-sm text-stone-600">
                      {searchTrainer.trim()
                        ? t('users.none_search', 'No hay resultados para tu búsqueda.')
                        : t('trainers.none', 'No hay entrenadores registrados.')}
                    </td>
                  </tr>
                ) : filteredTrainers.map(tr => (
                  <tr key={tr.id} className="hover:bg-stone-100 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-600">#{tr.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-stone-900">{tr.nombre}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-600">{tr.especialidad || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <InviteCodeCell
                        value={tr.invite_code}
                        onSave={(code) => saveTrainerInviteCode(tr.id, code)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-stone-600">{tr.capacidad_usuarios ?? 50}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end gap-2 flex-wrap">
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-2xl border border-stone-300 text-stone-700 bg-white hover:bg-stone-100 transition-colors"
                          onClick={() => startEditTrainer(tr)}
                        >
                          <Pencil className="w-3.5 h-3.5" aria-hidden />
                          {t('common.edit', 'Editar')}
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-2xl border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
                          onClick={() => handleDeleteTrainer(tr)}
                        >
                          <Trash2 className="w-3.5 h-3.5" aria-hidden />
                          {t('common.delete', 'Eliminar')}
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-2xl border border-lime-300 text-stone-800 bg-lime-50 hover:bg-lime-100 transition-colors"
                          onClick={() => setBrandingTrainer(tr)}
                        >
                          {t('wl.brand_btn', 'Marca')}
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-2xl border border-stone-300 text-stone-700 bg-white hover:bg-stone-100 transition-colors"
                          onClick={() => {
                            const list = users.filter(u => u.trainer_id === tr.id);
                            setAssignedTitle(t('companies.users_of_trainer', 'Usuarios del Entrenador: {name}').replace('{name}', tr.nombre));
                            setAssignedList(list);
                            setShowAssignedModal(true);
                          }}
                        >
                          {t('users.view_users', 'Ver usuarios')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isGymScoped && viewTab === 'sedes' && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden ui-light-panel">
          <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h4 className="text-md font-semibold text-stone-900">{t('users.sedes_title', 'Sedes de tu organización')}</h4>
              <p className="text-xs text-stone-500 mt-0.5">{t('users.sedes_subtitle', 'Cada sede opera con marca blanca y su propio administrador.')}</p>
            </div>
            {canCreateChildSedes && (
              <button type="button" className="btn-primary text-sm" onClick={() => setShowCreateSedeModal(true)}>
                {t('users.new_sede', 'Nueva sede')}
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">{t('common.name', 'Nombre')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">{t('common.city', 'Ciudad')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">{t('users.invite_code', 'Código invitación')}</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-stone-600 uppercase">{t('common.actions', 'Acciones')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {childSedes.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-sm text-stone-500">
                      {t('users.no_sedes', 'Aún no hay sedes. Crea la primera con marca blanca.')}
                    </td>
                  </tr>
                ) : childSedes.map((sede) => (
                  <tr key={sede.id} className="hover:bg-stone-50">
                    <td className="px-6 py-4 text-sm font-medium text-stone-900">{sede.nombre}</td>
                    <td className="px-6 py-4 text-sm text-stone-600">{sede.ciudad || '—'}</td>
                    <td className="px-6 py-4 text-sm">
                      <InviteCodeCell
                        value={sede.invite_code}
                        onSave={(code) => saveGymInviteCode(sede.id, code)}
                        onGenerate={() => generateGymInviteCode(sede.id)}
                      />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="admin-row-actions justify-end">
                        <button
                          type="button"
                          className="admin-action admin-action--neutral"
                          onClick={() => startEditSedeByGym(sede)}
                        >
                          {t('users.edit_sede', 'Editar sede')}
                        </button>
                        <button
                          type="button"
                          className="admin-action admin-action--neutral"
                          onClick={() => {
                            const list = users.filter((u) => (
                              userGymId(u) === Number(sede.id)
                              && isD28dConsumerFinalUser(u)
                            ));
                            setAssignedTitle(t('companies.users_of_gym', 'Usuarios del Gimnasio: {name}').replace('{name}', sede.nombre));
                            setAssignedList(list);
                            setShowAssignedModal(true);
                          }}
                        >
                          {t('users.view_users', 'Ver usuarios')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Table Section */}
      {viewTab === 'users' && !fitLiveTrainersTab && (
      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden ui-light-panel">
        <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-stone-900">{t('users.list_title', 'Listado de personas')}</p>
            {!loading && effectiveUsers.length > 0 && (
              <p className="text-xs text-stone-500 mt-0.5">
                {userSearch.trim()
                  ? t('users.search_count', '{shown} de {total}').replace('{shown}', sortedFilteredUsers.length).replace('{total}', effectiveUsers.length)
                  : t('users.total_count', '{total} registrados').replace('{total}', effectiveUsers.length)}
              </p>
            )}
            {isFoodClientsView && foodRosterStats ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {[
                  { id: 'linked', label: `En Food Plan (${foodRosterStats.linked})` },
                  { id: 'unlinked', label: `Sin perfil Food (${foodRosterStats.unlinked})` },
                  { id: 'all', label: `Todo el ecosistema (${foodRosterStats.total})` },
                ].map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                      foodRosterFilter === chip.id
                        ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                        : 'bg-white text-stone-600 border-slate-200'
                    }`}
                    onClick={() => setFoodRosterFilter(chip.id)}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2 items-center w-full sm:w-auto">
            {isTrainingUsersView ? (
              <select
                className="input w-auto min-w-[10rem]"
                value={promoFilter}
                onChange={(e) => setPromoFilter(e.target.value)}
                aria-label="Filtro promoción Viento Recio"
              >
                <option value="all">Todos</option>
                <option value="promo">Con promoción</option>
              </select>
            ) : null}
            <div className="relative w-full sm:max-w-md">
            <Search className="w-4 h-4 text-stone-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              className="input pl-9 w-full"
              placeholder={t('users.search_ph', 'Buscar por nombre, email, ID, rol, gimnasio o entrenador...')}
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              aria-label={t('users.search_aria', 'Buscar personas')}
            />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto admin-users-table-wrap">
          <table className="min-w-full divide-y divide-slate-200 admin-users-table">
            <thead className="bg-stone-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('common.id', 'ID')}</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('common.name', 'Nombre')}</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('common.email', 'Email')}</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('common.role', 'Rol')}</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">
                  {isTrainingUsersView
                    ? t('common.coach', 'Entrenador')
                    : t('common.gym', 'Gimnasio')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">
                  {isTrainingUsersView
                    ? t('users.coach_invite_code', 'Código coach')
                    : t('users.invite_code', 'Código invitación')}
                </th>
                {isTrainingUsersView && (
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Promo VR</th>
                )}
                {isTrainingUsersView && (
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('common.phone', 'Teléfono')}</th>
                )}
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('users.purchased_plan', 'Plan comprado')}</th>
                {isFoodClientsView && (
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Perfil Food</th>
                )}
                {showSubscriptionCols && (
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('users.sub_status', 'Estado')}</th>
                )}
                {showSubscriptionCols && (
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('users.days_remaining', 'Días restantes')}</th>
                )}
                <th scope="col" className="px-6 py-3 text-right text-xs font-semibold text-stone-600 uppercase tracking-wider admin-users-actions-col sticky right-0 bg-stone-50 z-10">{t('common.actions', 'Acciones')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {loading ? (
                    <tr><td colSpan={tableColSpan} className="px-6 py-4 text-center text-sm text-stone-600">{t('users.loading', 'Cargando usuarios...')}</td></tr>
              ) : effectiveUsers.length === 0 ? (
                 <tr><td colSpan={tableColSpan} className="px-6 py-4 text-center text-sm text-stone-600">{t('users.none', 'No hay usuarios registrados.')}</td></tr>
              ) : sortedFilteredUsers.length === 0 ? (
                 <tr><td colSpan={tableColSpan} className="px-6 py-4 text-center text-sm text-stone-600">{t('users.none_search', 'No hay resultados para tu búsqueda.')}</td></tr>
              ) : (
                sortedFilteredUsers.map(user => (
                  <tr key={user.id} className={`hover:bg-stone-100 transition-colors ${userNeedsActivation(user, gymById, gyms) ? 'bg-amber-50/50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-600">#{user.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-stone-900">{user.nombre}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-600">{user.email}</td>
                    <td className="px-6 py-4 whitespace-normal">
                      {(isGymSimpleForm || isSedeOperatorUser(user, gymById, gyms) || isSedeAdminCandidateUser(user, gymById, gyms)) ? (
                        <span className="badge badge-blue text-xs px-2 py-0.5 whitespace-nowrap">
                          {gymScopedRoleLabel(user, gymById)}
                        </span>
                      ) : (
                      <div className="flex flex-wrap gap-1">
                        {(user.roles && user.roles.length ? user.roles : [user.rol]).map(r => (
                          <span key={r} className={`badge ${getRoleBadgeClass(r)} text-xs px-2 py-0.5 whitespace-nowrap`}>
                            {formatRole(r)}
                          </span>
                        ))}
                      </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-600">
                      {isTrainingUsersView
                        ? userTrainerDisplayName(user, trainerById, t)
                        : userGymDisplayName(user, gymById, t)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-600">
                      {isTrainingUsersView ? (
                        (() => {
                          const code = userTrainerInviteCode(user, trainerById);
                          return code
                            ? <InviteCodeCell value={code} readOnly />
                            : <span className="text-stone-400">—</span>;
                        })()
                      ) : (
                      <div className="flex flex-col gap-1">
                        {userGymId(user) && gymById[userGymId(user)]?.invite_code && (
                          <span className="text-xs">
                            <span className="text-stone-500">Gym:</span>{' '}
                            <InviteCodeCell value={gymById[userGymId(user)].invite_code} readOnly />
                          </span>
                        )}
                        {user.trainer_id && trainerById[Number(user.trainer_id)]?.invite_code && (
                          <span className="text-xs">
                            <span className="text-stone-500">Coach:</span>{' '}
                            <InviteCodeCell value={trainerById[Number(user.trainer_id)].invite_code} readOnly />
                          </span>
                        )}
                        {!isGymScoped && !userGymId(user) && !user.trainer_id && user.module_access?.d28d && isSuperAdmin && (
                          <span className="text-xs text-stone-400">D28D</span>
                        )}
                        {!userGymId(user) && !user.trainer_id && !user.module_access?.d28d && (
                          <span className="text-stone-400">—</span>
                        )}
                      </div>
                      )}
                    </td>
                    {isTrainingUsersView && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {user.module_access?.training_promo?.code ? (
                          <span className="inline-flex items-center rounded-full bg-violet-100 text-violet-900 text-xs font-semibold px-2 py-0.5">
                            {user.module_access.training_promo.label || user.module_access.training_promo.code}
                          </span>
                        ) : (
                          <span className="text-stone-400">—</span>
                        )}
                      </td>
                    )}
                    {isTrainingUsersView && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-600">
                        {user.telefono || user.phone || <span className="text-stone-400">—</span>}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-600">
                      {(() => {
                        const planName = user.planId || user.subscription?.plan;
                        const plan = plans.find((p) => p.nombre === planName);
                        const program = programs.find((p) => p.id === plan?.program_id);
                        const coupleLabel = coupleLinkLabel(user, users);
                        if (!planName && !coupleLabel) return <span className="text-stone-400">—</span>;
                        return (
                          <div>
                            {planName ? (
                              <span className="font-medium text-stone-900">{planName}</span>
                            ) : null}
                            {program && (
                              <span className="block text-xs text-stone-500 capitalize">{program.name}</span>
                            )}
                            {coupleLabel ? (
                              <span className="block text-[10px] text-violet-700 mt-0.5 font-medium">
                                {coupleLabel}
                              </span>
                            ) : null}
                          </div>
                        );
                      })()}
                    </td>
                    {isFoodClientsView && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {hasFoodNestLink(user) ? (
                          <span className="badge badge-green text-xs">Sí</span>
                        ) : (
                          <span className="text-stone-400">No</span>
                        )}
                      </td>
                    )}
                    {showSubscriptionCols && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {showUserSubscriptionCols(user, gymById, gyms) ? (
                          <div>
                            <span className={`badge ${SUBSCRIPTION_ESTADO_BADGE[String(user.subscription?.estado || '').toLowerCase()] || 'badge-slate'} text-xs`}>
                              {formatSubscriptionEstado(user.subscription, t)}
                            </span>
                            {String(user.subscription?.estado || '').toLowerCase() === 'pendiente_habilitacion'
                              && user.subscription?.cycle_deferred_until
                              && !userRolesList(user).includes('usuario_final_sede')
                              && user.is_sede_final !== true && (
                              <span className="block text-[10px] text-sky-700 mt-1 max-w-[12rem]">
                                {t('users.cycle_deferred_short', 'Habilitación')}: {new Date(`${user.subscription.cycle_deferred_until}T12:00:00`).toLocaleDateString('es-CO')}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-stone-400">—</span>
                        )}
                      </td>
                    )}
                    {showSubscriptionCols && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-700 font-medium">
                        {showUserSubscriptionCols(user, gymById, gyms) ? formatDaysLeft(user.subscription, t) : '—'}
                      </td>
                    )}
                    <td className="px-4 py-4 text-right text-sm font-medium admin-users-actions-col sticky right-0 bg-white z-10">
                      <div className="admin-row-actions admin-row-actions--table">
                        {showSubActionButtons && !isTrainingCoachClient(user) && userNeedsActivation(user, gymById, gyms) && (
                          <button
                            type="button"
                            className="admin-action admin-action--success"
                            onClick={() => openActivateModal(user)}
                            title={t('users.activate_user', 'Activar usuario')}
                          >
                            <UserCheck className="w-4 h-4" />
                            <span>{t('users.activate_user', 'Activar')}</span>
                          </button>
                        )}
                        {isConsumerUserRow(user) && (() => {
                          const needsAct = userNeedsActivation(user, gymById, gyms);
                          const waUrl = consumerContactWaUrl(user, user.subscription, t, { needsActivation: needsAct });
                          if (waUrl) {
                            return (
                              <a
                                href={waUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="admin-action admin-action--whatsapp"
                                title={needsAct
                                  ? t('users.whatsapp_remind', 'Recordar pago por WhatsApp')
                                  : t('users.whatsapp_contact', 'Contactar por WhatsApp')}
                              >
                                <MessageCircle className="w-4 h-4" />
                                <span>WhatsApp</span>
                              </a>
                            );
                          }
                          return (
                            <button
                              type="button"
                              className="admin-action admin-action--whatsapp opacity-50 cursor-not-allowed"
                              disabled
                              title={t('users.whatsapp_no_phone', 'Sin teléfono registrado — edita el usuario para habilitar WhatsApp')}
                            >
                              <MessageCircle className="w-4 h-4" />
                              <span>WhatsApp</span>
                            </button>
                          );
                        })()}
                        {showSubActionButtons && isConsumerUserRow(user) && user.subscription?.has_account
                          && user.subscription?.active && !user.subscription?.pending && (
                              <button
                                type="button"
                                className="admin-action admin-action--pending"
                                onClick={() => setPendingTarget(user)}
                                title={t('users.mark_pending_title', 'Marcar pago pendiente')}
                              >
                                <Clock className="w-4 h-4" />
                                <span>{t('users.mark_pending_short', 'Pendiente')}</span>
                              </button>
                            )}
                        {showSubActionButtons && isConsumerUserRow(user) && user.subscription?.has_account
                          && user.subscription?.active && !user.subscription?.pending && (
                              <button
                                type="button"
                                className="admin-action admin-action--extend"
                                onClick={() => handleExtendUser(user)}
                                title={t('vigencias.extend', 'Ampliar días')}
                              >
                                <CalendarPlus className="w-4 h-4" />
                                <span>{t('users.extend_days', 'Ampliar')}</span>
                              </button>
                            )}
                        {showSubActionButtons && isConsumerUserRow(user) && user.subscription?.has_account
                          && user.subscription?.active && !user.subscription?.pending && (
                              <button
                                type="button"
                                className="admin-action admin-action--neutral"
                                onClick={() => handleDeactivateUser(user)}
                                title={t('users.deactivate_user', 'Desactivar usuario')}
                              >
                                <UserX className="w-4 h-4" />
                                <span>{t('users.deactivate_user', 'Desactivar')}</span>
                              </button>
                            )}
                        <button
                          type="button"
                          className="admin-action admin-action--edit"
                          onClick={() => startEdit(user)}
                          title={t('common.edit', 'Editar')}
                        >
                          <Pencil className="w-4 h-4" />
                          <span>{t('common.edit', 'Editar')}</span>
                        </button>
                        <button
                          type="button"
                          className="admin-action admin-action--password"
                          onClick={async () => {
                            const pwd = window.prompt(t('users.new_password_prompt', 'Nueva contraseña (mín. 6 caracteres):'));
                            if (!pwd) return;
                            if (pwd.length < 6) {
                              setError(t('users.password_min', 'La contraseña debe tener al menos 6 caracteres'));
                              return;
                            }
                            try {
                              await api.put(`/admin/users/${user.id}/password`, { password: pwd });
                              alert(t('users.password_updated', 'Contraseña actualizada'));
                            } catch {
                              setError(t('users.password_update_error', 'Error actualizando contraseña'));
                            }
                          }}
                          title={t('users.reset_password', 'Resetear contraseña')}
                        >
                          <KeyRound className="w-4 h-4" />
                          <span>{t('users.reset_password_short', 'Reset')}</span>
                        </button>
                        {canDeleteUsers && (
                        <button
                          type="button"
                          className="admin-action admin-action--danger"
                          onClick={() => handleDelete(user.id)}
                          disabled={currentUser && currentUser.id === user.id}
                          title={t('common.delete', 'Eliminar')}
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>{t('common.delete', 'Eliminar')}</span>
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}
      
      </>
      )}

      {/* Assigned Users Modal */}
      {showAssignedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200 w-full max-w-3xl">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h4 className="text-lg font-semibold text-stone-900">{assignedTitle}</h4>
              <button className="btn-secondary" onClick={() => setShowAssignedModal(false)}>{t('common.close', 'Cerrar')}</button>
            </div>
            <div className="p-5 max-h-[70vh] overflow-y-auto">
              {assignedList.length === 0 ? (
                <p className="text-sm text-stone-600">{t('users.no_assigned', 'No hay usuarios asociados.')}</p>
              ) : (
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-stone-600 uppercase">{t('common.id', 'ID')}</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-stone-600 uppercase">{t('common.name', 'Nombre')}</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-stone-600 uppercase">{t('common.email', 'Email')}</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-stone-600 uppercase">{t('common.plan', 'Plan')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {assignedList.map(u => (
                      <tr key={u.id} className="hover:bg-stone-50">
                        <td className="px-4 py-2 text-sm text-stone-700">#{u.id}</td>
                        <td className="px-4 py-2 text-sm text-stone-900">{u.nombre}</td>
                        <td className="px-4 py-2 text-sm text-stone-700">{u.email}</td>
                        <td className="px-4 py-2 text-sm text-stone-700">{u.planId || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end">
              <button className="btn-primary" onClick={() => setShowAssignedModal(false)}>{t('common.close', 'Cerrar')}</button>
            </div>
          </div>
        </div>
      )}

      {brandingTrainer && (
        <TrainerBrandingModal
          trainer={brandingTrainer}
          onClose={() => setBrandingTrainer(null)}
          onSaved={fetchResources}
        />
      )}

      {editingTrainer && (
        <div className="form-modal-overlay" role="dialog" aria-modal="true" onClick={() => !trainerEditSaving && setEditingTrainer(null)}>
          <div
            className="form-modal d28d-admin-modal ui-light-panel surface-readable max-w-lg w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 mb-4">
              <h3 className="text-lg font-semibold text-stone-900">{t('trainers.edit_title', 'Editar entrenador')}</h3>
              <button type="button" className="btn-secondary text-sm" onClick={() => setEditingTrainer(null)} disabled={trainerEditSaving}>
                {t('common.close', 'Cerrar')}
              </button>
            </div>
            <form onSubmit={saveTrainerEdit} className="space-y-3">
              <label className="block text-sm">
                <span className="font-semibold text-stone-800">{t('common.name', 'Nombre')}</span>
                <input
                  className="input mt-1 w-full"
                  required
                  value={trainerEditForm.nombre}
                  onChange={(e) => setTrainerEditForm((p) => ({ ...p, nombre: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className="font-semibold text-stone-800">{t('common.email', 'Email')}</span>
                <input
                  className="input mt-1 w-full"
                  type="email"
                  required
                  value={trainerEditForm.email}
                  onChange={(e) => setTrainerEditForm((p) => ({ ...p, email: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className="font-semibold text-stone-800">{t('trainers.specialty', 'Especialidad')}</span>
                <input
                  className="input mt-1 w-full"
                  value={trainerEditForm.especialidad}
                  onChange={(e) => setTrainerEditForm((p) => ({ ...p, especialidad: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className="font-semibold text-stone-800">{t('plans.capacity', 'Cupo Máx')}</span>
                <input
                  className="input mt-1 w-full"
                  type="number"
                  min={1}
                  value={trainerEditForm.capacidad_usuarios}
                  onChange={(e) => setTrainerEditForm((p) => ({ ...p, capacidad_usuarios: e.target.value }))}
                />
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="btn-secondary" onClick={() => setEditingTrainer(null)} disabled={trainerEditSaving}>
                  {t('common.cancel', 'Cancelar')}
                </button>
                <button type="submit" className="btn-primary" disabled={trainerEditSaving}>
                  {trainerEditSaving ? t('common.saving', 'Guardando…') : t('common.save', 'Guardar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ExtendVigenciaModal
        open={Boolean(extendTarget)}
        userName={extendTarget?.nombre}
        planName={extendTarget?.subscription?.plan || extendTarget?.planId}
        moduleLabel="D28D"
        saving={extendSaving}
        onClose={() => !extendSaving && setExtendTarget(null)}
        onConfirm={confirmExtendUser}
      />

      <UserActivateModal
        open={Boolean(activateTarget) && !isCoachActor && !isTrainingUsersView && !isTrainingCoachClient(activateTarget)}
        user={activateTarget}
        plan={activateTarget ? userPlanRecord(activateTarget, plans) : null}
        saving={activateSaving || subscriptionActivating}
        error={activateTarget ? error : ''}
        allowFinanceZero={isSuperAdmin}
        onClose={() => !activateSaving && !subscriptionActivating && setActivateTarget(null)}
        onConfirm={confirmActivateUser}
      />

      <MarkPendingPaymentModal
        open={Boolean(pendingTarget)}
        user={pendingTarget}
        saving={pendingSaving}
        onClose={() => !pendingSaving && setPendingTarget(null)}
        onConfirm={confirmMarkPending}
      />

      <EditUserModal
        open={Boolean(editingUser)}
        loading={editLoading}
        user={editingUser}
        formData={formData}
        setFormData={setFormData}
        saving={editSaving}
        error={error}
        onClose={closeEditModal}
        onSubmit={handleSubmit}
        plans={plans}
        cycles={cycles}
        programs={programs}
        trainers={trainers}
        gyms={memberAssignableGyms}
        isGymScoped={isGymScoped}
        isGymSimpleForm={isGymSimpleForm}
        isCoachActor={isCoachActor}
        coachTrainingPlans={coachTrainingPlans}
        isSuperAdmin={isSuperAdmin}
        isPlatformD28dAdmin={isD28dMemberAdminUi}
        scopeGymId={gymScopeId}
        scopeGymName={scopeGymName}
        gymById={gymById}
        roleOptionsForForm={roleOptionsForForm}
        coachModuleOptions={coachModuleOptions}
        formatRole={formatRole}
        showSubscriptionActions={canManageSubscription && !isCoachActor && !isTrainingUsersView}
        subscriptionActivating={subscriptionActivating}
        onActivate={(u) => openActivateModal(u)}
        onDeactivate={handleDeactivateUser}
        onExtend={handleExtendUser}
        parentSedePrograms={parentSedePrograms}
        sedeCatalogPlans={sedeCatalogPlans}
        onSaveSedeInviteCode={saveGymInviteCode}
        onGenerateSedeInviteCode={generateGymInviteCode}
        onSwitchToUserEdit={isSuperAdmin ? switchEditToUserMode : undefined}
      />

      {canCreateChildSedes && (
        <CreateVirtualGymModal
          open={showCreateSedeModal}
          parentGym={{
            id: gymScopeId,
            nombre: scopeGymName || scopeGym?.nombre,
            name: scopeGymName || scopeGym?.nombre,
          }}
          onClose={() => setShowCreateSedeModal(false)}
          onCreated={() => {
            fetchResources();
            fetchUsers();
          }}
        />
      )}

      {isPlatformD28dActor && createSedeParent ? (
        <CreateVirtualGymModal
          open={Boolean(createSedeParent)}
          parentGym={{
            id: createSedeParent.id,
            nombre: createSedeParent.nombre,
            name: createSedeParent.brand_name || createSedeParent.nombre,
          }}
          onClose={() => setCreateSedeParent(null)}
          onCreated={() => {
            setCreateSedeParent(null);
            fetchResources();
            fetchUsers();
          }}
        />
      ) : null}

      {isPlatformD28dActor && (
        <CreateVirtualGymModal
          open={showCreateVirtualGymModal}
          onClose={() => setShowCreateVirtualGymModal(false)}
          onCreated={() => {
            setShowCreateVirtualGymModal(false);
            fetchResources();
            fetchUsers();
          }}
        />
      )}

      <OrgProvisionSuccessModal
        open={Boolean(sedeProvisionResult)}
        onClose={() => setSedeProvisionResult(null)}
        title={sedeProvisionResult?.title}
        inviteCode={sedeProvisionResult?.inviteCode}
        adminCredentials={sedeProvisionResult?.adminCredentials}
      />
    </div>
  );
}
